import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
// NOTE: Team switching must not reset selection; refresh is guarded against loops.
// P0: Toast spam prevented via unique error tracking + max retries
// P0 FIX: getStoredUser() creates new object on each render; use initialLoadDone ref to prevent infinite re-renders
// P0 TENANT ISOLATION: Now uses TenantContext for URL-backed tenant state
// P1 URL TABS: Supports deep-linking via ?tab= query parameter

import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { getStoredUser } from "@/lib/auth";
import { useTenant } from "@/contexts/TenantContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
// P4-WP-7C.2: pure sparse-override transforms (no role/plan policy in JS).
import {
  overridesFromView,
  isInherited,
  setOverride,
  clearOverride,
  displayLevel,
  isBlockedByPlan,
  isBlockedByGovernance,
  countChanges,
  serializeOverrides,
} from "@/lib/adminModuleOverrides";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { usePageTitle } from "@/contexts/PageTitleContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import { toast } from "@/hooks/use-toast";
import { useHasModule, useIsSuperAdmin, usePermissions } from "@/hooks/usePermissions";
import { Plus, Mail, Users, Shield, Copy, Trash2, Sparkles, TestTubeDiagonal, Tag, Webhook, Route, Activity, BarChart3, Layers, AlertTriangle, RefreshCw, Inbox, Calendar, Map, AlertOctagon, Link2, CheckCircle2, Search, Hash, Send, TrendingUp, ChevronDown, ChevronUp, X } from "lucide-react";
import LeadSourceAdminManager from "@/components/LeadSourceAdminManager";
import VerticalMappingsManager from "@/components/VerticalMappingsManager";
import IntakeEventsViewer from "@/components/IntakeEventsViewer";
import OutboundEventsViewer from "@/components/OutboundEventsViewer";  // P1: Outbound events diagnostics
import QueueJobsViewer from "@/components/QueueJobsViewer";  // MA-ADMIN-009: Queue operator tooling
import BenchmarkManager from "@/components/BenchmarkManager";
import WorkspaceAdminManager from "@/components/WorkspaceAdminManager";
import UserWorkspaceAssignment from "@/components/UserWorkspaceAssignment";
import CalendarRoutingManager from "@/components/CalendarRoutingManager";
import VocabularyManager from "@/components/VocabularyManager";  // P1: Vocabulary management
import WebhookKeyCard from "@/components/WebhookKeyCard";
import InboundSetupManager from "@/components/InboundSetupManager";
import ComplianceSettingsManager from "@/components/admin/ComplianceSettingsManager";
import OutboundSetupManager from "@/components/admin/OutboundSetupManager";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Label } from "@/components/ui/label";
import {
  DEFAULT_INVITE_ROLE,
  inviteRoleOptionsForActor,
  resolveInviteRole,
} from "@/lib/adminInviteRoles";

// P4-WP-8C.5: invitation timestamps are ISO strings from the governed read
// surface. A malformed or missing value renders as nothing rather than
// "Invalid Date" -- the invitation is still actionable without its date.
// P4-WP-8C.6: the human-readable identity of a pending invitation.
// Invitations created before this package carry no names; that is the supported
// legacy shape, so the email address is the fallback rather than a placeholder.
function pendingDisplayName(invite) {
  const full = [invite?.pending_first_name, invite?.pending_last_name]
    .filter((part) => typeof part === "string" && part.trim())
    .join(" ")
    .trim();
  return full || invite?.pending_user_email || "";
}

function pendingHasName(invite) {
  return Boolean(
    (invite?.pending_first_name || "").trim() || (invite?.pending_last_name || "").trim()
  );
}


function formatInvitedAt(isoString) {
  if (!isoString) return "";
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}


function RoleBadge({ role }) {
  // MA-PHASE11A5-ADMIN-ROLE-AND-403-FIX-001: Support all canonical membership roles
  const label =
    role === "super_admin"
      ? "Super Admin"
      : role === "owner"
        ? "Owner"
        : role === "admin"
          ? "Admin"
          : role === "member"
            ? "Member"
            : role === "sales_agent"
              ? "Sales Agent"
              : role === "booking_agent"
                ? "Booking Agent"
                : role === "agent" // legacy
                  ? "Sales Agent"
                  : role === "unknown"
                    ? "Unknown"
                    : "Sales Agent"; // fallback
  
  const cls =
    role === "super_admin"
      ? "bg-indigo-500/20 text-indigo-100 ring-indigo-400/20"
      : role === "owner"
        ? "bg-amber-500/20 text-amber-100 ring-amber-400/20"
        : role === "admin"
          ? "bg-indigo-500/15 text-indigo-200 ring-indigo-400/20"
          : "bg-white/10 text-slate-100 ring-white/10";
  
  return (
    <Badge className={`${cls} ring-1`} data-testid={`role-badge-${role}`}>
      {label}
    </Badge>
  );
}

// ============================================================================
// QUARANTINE QUEUE CONTENT COMPONENT
// ============================================================================

// Format date for display
function formatQuarantineDate(isoString) {
  if (!isoString) return "—";
  try {
    const d = new Date(isoString);
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return isoString;
  }
}

// Reason badge colors
function getQuarantineReasonBadge(reason) {
  const labels = {
    missing_lead_id: { label: "Missing Lead", color: "bg-amber-500/20 text-amber-200 ring-amber-400/20" },
    invalid_lead_id: { label: "Invalid Lead", color: "bg-red-500/20 text-red-200 ring-red-400/20" },
    missing_household: { label: "Missing Household", color: "bg-orange-500/20 text-orange-200 ring-orange-400/20" },
    ghl_contact_not_found: { label: "Contact Not Found", color: "bg-purple-500/20 text-purple-200 ring-purple-400/20" },
    unknown: { label: "Unknown", color: "bg-white/10 text-slate-200 ring-white/10" },
  };
  const cfg = labels[reason] || labels.unknown;
  return (
    <Badge className={`${cfg.color} ring-1 text-xs`} data-testid={`reason-badge-${reason}`}>
      {cfg.label}
    </Badge>
  );
}

// Status badge
function getQuarantineStatusBadge(item) {
  if (item.is_quarantined) {
    return (
      <Badge className="bg-red-500/20 text-red-200 ring-1 ring-red-400/20 text-xs" data-testid="status-quarantined">
        Quarantined
      </Badge>
    );
  }
  return (
    <Badge className="bg-emerald-500/20 text-emerald-200 ring-1 ring-emerald-400/20 text-xs" data-testid="status-resolved">
      Resolved
    </Badge>
  );
}

// Resolve Dialog Component
function QuarantineResolveDialog({ open, onOpenChange, appointment, suggestedLead, onResolved }) {
  const [leadId, setLeadId] = useState("");
  const [notes, setNotes] = useState("");
  const [resolving, setResolving] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (open) {
      setLeadId(suggestedLead?.id || "");
      setNotes("");
      setSearchQuery("");
      setSearchResults([]);
    }
  }, [open, suggestedLead]);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const res = await api.get("/leads", { params: { q: searchQuery, limit: 10 } });
      setSearchResults(res.data?.items || []);
    } catch {
      toast({ title: "Search failed", variant: "destructive" });
    } finally {
      setSearching(false);
    }
  };

  const handleResolve = async () => {
    if (!leadId) {
      toast({ title: "Please select or enter a lead ID", variant: "destructive" });
      return;
    }
    setResolving(true);
    try {
      await api.post(`/admin/quarantine/appointments/${appointment.id}/resolve`, {
        lead_id: leadId,
        notes: notes || undefined,
      });
      toast({ title: "Quarantine resolved successfully" });
      onResolved();
      onOpenChange(false);
    } catch (e) {
      toast({ title: "Failed to resolve", description: e?.response?.data?.detail || "Please try again", variant: "destructive" });
    } finally {
      setResolving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-700 max-w-lg" data-testid="resolve-dialog">
        <DialogHeader>
          <DialogTitle className="text-white">Resolve Quarantine</DialogTitle>
          <DialogDescription className="text-slate-400">
            Link this appointment to a lead to resolve the quarantine.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-2xl bg-white/5 p-3 text-sm ring-1 ring-white/10">
            <div className="text-slate-400">Appointment</div>
            <div className="text-white font-mono text-xs">{appointment?.id}</div>
            {appointment?.start_at && (
              <div className="text-slate-300 mt-1">{formatQuarantineDate(appointment.start_at)}</div>
            )}
          </div>

          {suggestedLead && (
            <div className="rounded-2xl bg-emerald-500/10 p-3 ring-1 ring-emerald-400/20">
              <div className="flex items-center gap-2 text-emerald-300 text-sm font-medium mb-2">
                <CheckCircle2 className="w-4 h-4" /> Suggested Match Found
              </div>
              <div className="text-white text-sm">{suggestedLead.full_name || "Unknown"}</div>
              <div className="text-slate-400 text-xs">{suggestedLead.phone_number || suggestedLead.email || suggestedLead.id}</div>
              <Button
                size="sm"
                variant="success"
                className="mt-2"
                onClick={() => setLeadId(suggestedLead.id)}
                data-testid="use-suggested-lead-btn"
              >
                Use This Lead
              </Button>
            </div>
          )}

          <div className="space-y-2">
            <Label className="text-slate-300">Lead ID</Label>
            <Input
              value={leadId}
              onChange={(e) => setLeadId(e.target.value)}
              placeholder="Enter lead ID..."
              className="bg-white/5 text-slate-100 ring-1 ring-white/10"
              data-testid="lead-id-input"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-slate-300">Search for Lead</Label>
            <div className="flex gap-2">
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name, phone, email..."
                className="bg-white/5 text-slate-100 ring-1 ring-white/10"
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                data-testid="lead-search-input"
              />
              <Button
                variant="ghost"
                size="icon"
                onClick={handleSearch}
                disabled={searching}
                data-testid="lead-search-btn"
              >
                <Search className="w-4 h-4" />
              </Button>
            </div>
            
            {searchResults.length > 0 && (
              <div className="rounded-xl bg-white/5 ring-1 ring-white/10 max-h-40 overflow-y-auto">
                {searchResults.map((lead) => (
                  <button
                    key={lead.id}
                    className="w-full px-3 py-2 text-left hover:bg-white/10 border-b border-white/5 last:border-b-0"
                    onClick={() => { setLeadId(lead.id); setSearchResults([]); }}
                    data-testid={`search-result-${lead.id}`}
                  >
                    <div className="text-white text-sm">{lead.full_name || "Unknown"}</div>
                    <div className="text-slate-400 text-xs">{lead.phone_number || lead.email || lead.id}</div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label className="text-slate-300">Notes (optional)</Label>
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Resolution notes..."
              className="bg-white/5 text-slate-100 ring-1 ring-white/10"
              data-testid="notes-input"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="secondary"
            onClick={() => onOpenChange(false)}
            data-testid="resolve-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleResolve}
            disabled={resolving || !leadId}
            data-testid="resolve-confirm-btn"
          >
            {resolving ? "Resolving..." : "Resolve Quarantine"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Main Quarantine Queue Content
function QuarantineQueueContent({ tenantId }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [includeResolved, setIncludeResolved] = useState(false);
  const [offset, setOffset] = useState(0);
  const limit = 15;

  const [batchResolving, setBatchResolving] = useState(false);
  const [lastBatchResult, setLastBatchResult] = useState(null);

  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);

  const fetchQuarantines = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/quarantine/appointments", {
        params: { limit, offset, include_resolved: includeResolved },
      });
      setItems(res.data?.items || []);
      setTotal(res.data?.total || 0);
    } catch (e) {
      toast({ title: "Failed to load quarantine queue", description: e?.response?.data?.detail || "Please try again", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [offset, includeResolved]);

  useEffect(() => {
    fetchQuarantines();
  }, [fetchQuarantines, tenantId]);

  const handleBatchResolve = async () => {
    setBatchResolving(true);
    try {
      const res = await api.post("/admin/quarantine/batch-resolve", null, {
        params: { max_age_hours: 24, batch_size: 100 },
      });
      setLastBatchResult(res.data);
      toast({
        title: "Batch Resolve Complete",
        description: `Resolved: ${res.data?.resolved || 0}, No match: ${res.data?.no_match || 0}, Failed: ${res.data?.failed || 0}`,
      });
      fetchQuarantines();
    } catch (e) {
      toast({ title: "Batch resolve failed", description: e?.response?.data?.detail || "Please try again", variant: "destructive" });
    } finally {
      setBatchResolving(false);
    }
  };

  const openResolveDialog = (appointment) => {
    setSelectedAppointment(appointment);
    setResolveDialogOpen(true);
  };

  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(offset / limit) + 1;
  const quarantinedCount = items.filter(i => i.is_quarantined).length;

  return (
    <Card className="bg-white/5 border-white/10" data-testid="quarantine-queue-content">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-xl text-white flex items-center gap-2">
              <AlertOctagon className="h-5 w-5 text-indigo-400" />
              Quarantined Appointments
            </CardTitle>
            <div className="text-sm text-slate-400 mt-1">
              Appointments that failed data integrity validation
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              onClick={fetchQuarantines}
              disabled={loading}
              size="sm"
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button
              onClick={handleBatchResolve}
              disabled={batchResolving || quarantinedCount === 0}
              size="sm"
              data-testid="batch-resolve-btn"
            >
              {batchResolving ? "Resolving..." : "Batch Resolve"}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Stats Row */}
        <div className="flex items-center gap-4">
          <Badge className="bg-red-500/20 text-red-200 ring-1 ring-red-400/20">
            Quarantined: {quarantinedCount}
          </Badge>
          <Badge className="bg-slate-500/20 text-slate-200 ring-1 ring-slate-400/20">
            Total: {total}
          </Badge>
          <label className="flex items-center gap-2 text-sm text-slate-400 ml-auto">
            <Switch
              checked={includeResolved}
              onCheckedChange={(checked) => {
                setIncludeResolved(checked);
                setOffset(0);
              }}
              className="data-[state=checked]:bg-indigo-500"
            />
            Show resolved
          </label>
        </div>

        {/* Last Batch Result */}
        {lastBatchResult && (
          <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
            <div className="text-sm text-emerald-300">
              Last Batch: <span className="font-medium">{lastBatchResult.resolved}</span> resolved,{" "}
              <span className="font-medium">{lastBatchResult.no_match}</span> no match,{" "}
              <span className="font-medium">{lastBatchResult.failed}</span> failed
            </div>
          </div>
        )}

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-6 h-6 text-slate-400 animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-slate-400" data-testid="empty-state">
            <Inbox className="w-12 h-12 mb-4 opacity-50" />
            <div className="text-lg font-medium">No quarantined appointments</div>
            <div className="text-sm mt-1">All appointments have valid lead linkage</div>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead className="text-slate-400">Quarantined At</TableHead>
                <TableHead className="text-slate-400">Appointment</TableHead>
                <TableHead className="text-slate-400">Contact</TableHead>
                <TableHead className="text-slate-400">Reason</TableHead>
                <TableHead className="text-slate-400">Suggested Match</TableHead>
                <TableHead className="text-slate-400">Status</TableHead>
                <TableHead className="text-slate-400 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                  <TableRow key={item.id} className="border-white/10 hover:bg-white/5" data-testid={`quarantine-row-${item.id}`}>
                    <TableCell className="text-slate-300 text-sm">{formatQuarantineDate(item.quarantined_at)}</TableCell>
                    <TableCell>
                      <div className="text-white text-sm font-mono truncate max-w-32">{item.id?.slice(0, 8)}...</div>
                      {item.start_at && <div className="text-slate-400 text-xs mt-1">{formatQuarantineDate(item.start_at)}</div>}
                    </TableCell>
                    <TableCell>
                      {item.lead_snapshot?.full_name && <div className="text-white text-sm">{item.lead_snapshot.full_name}</div>}
                      <div className="text-slate-400 text-xs">{item.lead_snapshot?.phone_number || item.lead_snapshot?.email || item.ghl_contact_id?.slice(0, 12) || "—"}</div>
                    </TableCell>
                    <TableCell>
                      {getQuarantineReasonBadge(item.quarantine_reason)}
                      {item.quarantine_detail && <div className="text-slate-500 text-xs mt-1 max-w-40 truncate" title={item.quarantine_detail}>{item.quarantine_detail}</div>}
                    </TableCell>
                    <TableCell>
                      {item.suggested_lead ? (
                        <div className="text-emerald-400 text-sm">
                          <div>{item.suggested_lead.full_name || "Match found"}</div>
                          <div className="text-xs text-slate-400">{item.suggested_lead.phone_number || item.suggested_lead.email || item.suggested_lead.id?.slice(0, 12)}</div>
                        </div>
                      ) : <span className="text-slate-500 text-sm">—</span>}
                    </TableCell>
                    <TableCell>
                      {getQuarantineStatusBadge(item)}
                      {item.resolved_at && <div className="text-slate-500 text-xs mt-1">{formatQuarantineDate(item.resolved_at)}</div>}
                    </TableCell>
                    <TableCell className="text-right">
                      {item.is_quarantined ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openResolveDialog(item)}
                          data-testid={`resolve-btn-${item.id}`}
                        >
                          {item.suggested_lead ? <><CheckCircle2 className="w-3 h-3 mr-1" />Resolve</> : <><Link2 className="w-3 h-3 mr-1" />Link Lead</>}
                        </Button>
                      ) : (
                        <Badge className="bg-white/10 text-slate-400 ring-1 ring-white/10 text-xs">{item.resolved_by || "Resolved"}</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-4 border-t border-white/10">
              <div className="text-sm text-slate-400">Page {currentPage} of {totalPages} ({total} total)</div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setOffset(Math.max(0, offset - limit))}
                  disabled={offset === 0}
                  data-testid="prev-page-btn"
                >
                  Previous
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setOffset(offset + limit)}
                  disabled={offset + limit >= total}
                  data-testid="next-page-btn"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
      </CardContent>

      {/* Resolve Dialog */}
      <QuarantineResolveDialog
        open={resolveDialogOpen}
        onOpenChange={setResolveDialogOpen}
        appointment={selectedAppointment}
        suggestedLead={selectedAppointment?.suggested_lead}
        onResolved={fetchQuarantines}
      />
    </Card>
  );
}

// ============================================================================
// MA-ADMIN-ACCESS-CENTER-UI-148: ACCESS CENTER COMPONENT (PRODUCTION POLISH)
// ============================================================================

// Module groupings for visual organization with icons and descriptions
/**
 * P4-WP-8C.3: the one Account invitation dialog.
 *
 * AdminConsole had two, with divergent role lists and shared mutable state --
 * which is how one came to offer `agent` under the label "Sales Agent" while
 * the other offered `member`. Both entry points now render this, so a role
 * list can no longer drift between them.
 *
 * Options come from `inviteRoleOptionsForActor`; the server re-decides the
 * same question. Offering nothing to an actor without invitation authority is
 * a UI courtesy, never the enforcement.
 */
function InviteMemberDialog({
  open,
  onOpenChange,
  actorRole,
  title,
  accountName,
  firstName,
  onFirstNameChange,
  lastName,
  onLastNameChange,
  email,
  onEmailChange,
  role,
  onRoleChange,
  availableWorkspaces,
  selectedWorkspaces,
  onToggleWorkspace,
  onSubmit,
  onCancel,
  submitting,
  triggerLabel,
  triggerVariant,
  triggerTestId,
  emailTestId,
  roleTestId,
  submitTestId,
  workspaceTestIdPrefix,
}) {
  const roleOptions = inviteRoleOptionsForActor(actorRole);
  const activeWorkspaces = (availableWorkspaces || []).filter(
    (ws) => ws.status === "active" || !ws.status
  );

  // P4-WP-8C.6: one field style, so inputs and the select read as one control
  // family rather than three unrelated widgets.
  const FIELD =
    "bg-slate-800/60 border border-slate-600/60 text-slate-100 placeholder:text-slate-500 " +
    "focus-visible:ring-1 focus-visible:ring-indigo-400/60 focus-visible:border-indigo-400/60";
  const LABEL = "text-xs font-medium uppercase tracking-wide text-slate-400";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant={triggerVariant} className={triggerVariant ? "self-start" : undefined} data-testid={triggerTestId}>
          <Mail className="mr-2 h-4 w-4" /> {triggerLabel}
        </Button>
      </DialogTrigger>
      {/* P4-WP-8C.6: the solid MissionAssist surface, not `glass`.
          `glass` is rgba(255,255,255,0.06) + blur over a black/80 overlay --
          translucent white over black reads as flat grey, which is what made
          this modal look foreign to the rest of Admin. App.css already defines
          the solid treatment for exactly this reason ("prevent backdrop
          bleed-through"); DialogContent's own base is slate-900, so removing
          the override is most of the fix. No new tokens, no new theme. */}
      <DialogContent
        className="modal-solid border-slate-700/80 shadow-2xl sm:rounded-xl"
        data-testid="invite-member-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold text-slate-50" data-testid="invite-dialog-title">
            {title}
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-400" data-testid="invite-dialog-subtitle">
            Invite someone to join {accountName || "this Account"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 pt-1">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className={LABEL} htmlFor="invite-first-name">First Name</label>
              <Input
                id="invite-first-name"
                value={firstName}
                onChange={(e) => onFirstNameChange(e.target.value)}
                className={FIELD}
                placeholder="Jane"
                maxLength={80}
                data-testid="invite-first-name-input"
              />
            </div>
            <div className="space-y-1.5">
              <label className={LABEL} htmlFor="invite-last-name">Last Name</label>
              <Input
                id="invite-last-name"
                value={lastName}
                onChange={(e) => onLastNameChange(e.target.value)}
                className={FIELD}
                placeholder="Smith"
                maxLength={80}
                data-testid="invite-last-name-input"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className={LABEL} htmlFor="invite-email">Email Address</label>
            <Input
              id="invite-email"
              type="email"
              value={email}
              onChange={(e) => onEmailChange(e.target.value)}
              className={FIELD}
              placeholder="jane@example.com"
              data-testid={emailTestId}
            />
          </div>

          <div className="space-y-1.5">
            <label className={LABEL} htmlFor="invite-role">Role</label>
            <Select value={role} onValueChange={onRoleChange}>
              <SelectTrigger id="invite-role" className={FIELD} data-testid={roleTestId}>
                <SelectValue placeholder="Role" />
              </SelectTrigger>
              <SelectContent>
                {roleOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="text-xs text-slate-500" data-testid="invite-role-help">
              Determines what this person can access in this Account.
            </div>
          </div>

          {activeWorkspaces.length > 0 && (
            <div className="space-y-2">
              <div className={LABEL}>Assign to Workspaces (optional)</div>
              <div className="max-h-32 space-y-2 overflow-y-auto rounded-lg border border-slate-600/60 bg-slate-800/40 p-3">
                {activeWorkspaces.map((ws) => (
                  <div key={ws.id} className="flex items-center gap-2">
                    <Checkbox
                      id={`invite-ws-${ws.id}`}
                      checked={selectedWorkspaces.includes(ws.id)}
                      onCheckedChange={() => onToggleWorkspace(ws.id)}
                      data-testid={`${workspaceTestIdPrefix}${ws.id}`}
                    />
                    <label htmlFor={`invite-ws-${ws.id}`} className="cursor-pointer text-sm text-slate-200">
                      {ws.name}
                    </label>
                  </div>
                ))}
              </div>
              <div className="text-xs text-slate-500" data-testid="invite-workspace-help">
                If no workspaces are selected, this person gets default workspace access.
              </div>
            </div>
          )}

          {/* P4-WP-8C.6: says what actually happens. It can promise an email
              because WP-8C.6 dispatches one; before this package it would have
              been a false claim, which is why the old copy was corrected. */}
          <div
            className="rounded-lg border border-indigo-400/20 bg-indigo-500/10 p-3"
            data-testid="invite-what-happens-next"
          >
            <div className="text-sm font-medium text-indigo-100">What happens next?</div>
            <p className="mt-1 text-xs leading-relaxed text-slate-300">
              MissionAssist sends an invitation email with a link to create their
              MissionAssist account and join {accountName || "this Account"}. The
              invitation stays <span className="text-slate-100">Pending</span> until
              they accept it, and you can cancel it at any time.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={onCancel} data-testid="invite-cancel-button">
            Cancel
          </Button>
          <Button
            onClick={onSubmit}
            disabled={submitting || !email.trim() || !roleOptions.length}
            className="bg-indigo-600 text-white hover:bg-indigo-500"
            data-testid={submitTestId}
          >
            {submitting ? "Sending..." : "Send Invite"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const MODULE_GROUPS = {
  analytics: {
    label: "Analytics",
    description: "Performance insights and visibility",
    icon: BarChart3,
    modules: ["war_room", "funnels"],
    color: "indigo"
  },
  crm: {
    label: "CRM",
    description: "Customer relationship management",
    icon: Users,
    modules: ["leads", "appointments", "calendar", "clients"],
    color: "emerald"
  },
  automation: {
    label: "Automation",
    description: "Execution and workflow systems",
    icon: Activity,
    modules: ["playbooks", "runs"],
    color: "violet"
  },
  strategy: {
    label: "Strategy",
    description: "Planning and optimization",
    icon: TrendingUp,
    modules: ["profit_paths", "experiments"],
    color: "amber"
  },
  marketing: {
    label: "Marketing",
    description: "Campaigns and growth channels",
    icon: Sparkles,
    modules: ["landing_pages", "campaigns", "advertising"],
    color: "rose"
  },
  admin: {
    label: "Admin & System",
    description: "Configuration and governance",
    icon: Shield,
    modules: ["commission_profiles", "admin", "settings"],
    color: "slate"
  }
};

// Module display labels
const MODULE_LABELS = {
  war_room: "War Room",
  leads: "Leads",
  appointments: "Appointments",
  calendar: "Calendar",
  clients: "Clients",
  playbooks: "Playbooks",
  runs: "Runs",
  profit_paths: "Profit Paths",
  funnels: "Funnels",
  landing_pages: "Landing Pages",
  campaigns: "Campaigns",
  advertising: "Advertising",
  experiments: "Experiments",
  commission_profiles: "Commission Profiles",
  admin: "Admin",
  settings: "Settings"
};

// Valid access levels per module (must match backend registry)
// P4-WP-7C.2: human labels for access levels, used when telling the admin what
// a module's access actually is ("Inherit (Full)") rather than only which
// button is lit.
const MODULE_LEVEL_LABELS = {
  off: "Off",
  read: "Read",
  write: "Write",
  full: "Full",
};

const MODULE_ACCESS_LEVELS = {
  war_room: ["off", "full"],
  leads: ["off", "full"],
  calendar: ["off", "full"],
  runs: ["off", "full"],
  campaigns: ["off", "full"],
  advertising: ["off", "full"],
  experiments: ["off", "full"],
  commission_profiles: ["off", "full"],
  admin: ["off", "full"],
  settings: ["off", "full"],
  funnels: ["off", "full"],
  landing_pages: ["off", "full"],
  appointments: ["off", "read", "write", "full"],
  playbooks: ["off", "read", "full"],
  profit_paths: ["off", "read", "full"],
  clients: ["off", "full"]
};

// Access Center Content Component (MA-ADMIN-ACCESS-CENTER-UI-149: PREMIUMIZATION PASS)
function AccessCenterContent({ users, tenantId }) {
  const [selectedUserId, setSelectedUserId] = useState(null);
  // P4-WP-7C.2: the editor holds the SPARSE override set, not a dense matrix.
  //
  // `moduleView` is the backend's structured per-module answer (effective /
  // inherited / override / blocked_by_plan). `moduleOverrides` holds ONLY the
  // explicit overrides, so a module the admin leaves alone is simply absent
  // and stays inherited when saved.
  const [moduleView, setModuleView] = useState({});
  const [moduleOverrides, setModuleOverrides] = useState({});
  const [originalOverrides, setOriginalOverrides] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [collapsedGroups, setCollapsedGroups] = useState({});

  const selectedUser = useMemo(
    () => users.find((u) => u.id === selectedUserId),
    [users, selectedUserId]
  );

  // Filter users by search query
  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return users;
    const query = searchQuery.toLowerCase();
    return users.filter(
      (u) =>
        u.name?.toLowerCase().includes(query) ||
        u.email?.toLowerCase().includes(query)
    );
  }, [users, searchQuery]);

  // Calculate changes count
  const changesCount = useMemo(() => {
    return countChanges(originalOverrides, moduleOverrides);
  }, [moduleOverrides, originalOverrides]);

  const hasChanges = changesCount > 0;

  // Auto-select first user on load
  useEffect(() => {
    if (filteredUsers.length > 0 && !selectedUserId) {
      setSelectedUserId(filteredUsers[0].id);
    }
  }, [filteredUsers, selectedUserId]);

  // Load module permissions when user changes
  useEffect(() => {
    const loadModules = async () => {
      if (!selectedUserId) return;

      setLoading(true);
      try {
        const res = await api.get(`/admin/users/${selectedUserId}/modules`);
        const view = res.data || {};
        const overrides = overridesFromView(view);
        setModuleView(view);
        setModuleOverrides(overrides);
        setOriginalOverrides(overrides);
      } catch (e) {
        toast({
          title: "Failed to load permissions",
          description: e?.response?.data?.detail || "Please try again",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    loadModules();
  }, [selectedUserId]);

  const handleModuleChange = (moduleName, newLevel) => {
    setModuleOverrides((prev) => setOverride(prev, moduleName, newLevel));
  };

  // P4-WP-7C.2: "return this module to its role default" -- deletes the key
  // so the module is omitted from the next save and inherits again.
  const handleModuleInherit = (moduleName) => {
    setModuleOverrides((prev) => clearOverride(prev, moduleName));
  };

  // Distinct from the above: discards UNSAVED edits and restores the override
  // set exactly as it was loaded. It does not reset anything to inheritance.
  const handleReset = () => {
    setModuleOverrides(originalOverrides);
  };

  const handleSave = async () => {
    if (!selectedUserId || !hasChanges) return;

    setSaving(true);
    try {
      // Only the explicit overrides go over the wire. Everything omitted
      // inherits -- that omission is the whole point of WP-7C.2.
      const res = await api.put(`/admin/users/${selectedUserId}/modules`, {
        modules: serializeOverrides(moduleOverrides),
      });
      toast({ title: "Permissions updated successfully" });
      setOriginalOverrides(moduleOverrides);
      // Re-read so effective/inherited/blocked reflect what was just stored.
      try {
        const fresh = await api.get(`/admin/users/${selectedUserId}/modules`);
        if (fresh?.data) setModuleView(fresh.data);
      } catch (_) {
        // Non-fatal: the save succeeded; the view refreshes on next select.
        void res;
      }
    } catch (e) {
      toast({
        title: "Failed to update permissions",
        description: e?.response?.data?.detail || "Please try again",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const toggleGroup = (groupKey) => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }));
  };

  return (
    <div className="relative">
      {/* MA-UI-149: Split into TWO DISTINCT GLASS CARDS */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 pb-24">
        
        {/* LEFT CARD: TEAM DIRECTORY (Premium Glass Card - Auto Height) */}
        <Card className="lg:col-span-4 self-start bg-gradient-to-br from-white/10 to-white/5 border-white/20 shadow-2xl shadow-black/20">
          <CardHeader className="pb-4 border-b border-white/10">
            <div className="flex items-center justify-between mb-4">
              <CardTitle className="text-lg font-semibold text-white">Account Members</CardTitle>
              <div className="text-xs font-medium text-slate-400 bg-white/5 px-2.5 py-1 rounded-full">
                {filteredUsers.length} member{filteredUsers.length !== 1 ? 's' : ''}
              </div>
            </div>
            
            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <Input
                type="text"
                placeholder="Search members..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:bg-white/10 focus:border-indigo-400/50 transition-all"
                data-testid="access-search-members"
              />
            </div>
          </CardHeader>

          <CardContent className="pt-4 pb-4">
            {/* User List */}
            <div className="space-y-2 max-h-[520px] overflow-y-auto pr-1">
              {filteredUsers.map((u) => {
                const isSelected = selectedUserId === u.id;
                return (
                  <button
                    key={u.id}
                    onClick={() => setSelectedUserId(u.id)}
                    className={`
                      group w-full text-left p-4 rounded-xl border transition-all duration-200
                      ${
                        isSelected
                          ? "bg-gradient-to-br from-indigo-500/25 to-purple-500/15 border-indigo-400/60 ring-2 ring-indigo-400/40 shadow-lg"
                          : "bg-white/[0.03] border-white/10 hover:bg-white/[0.08] hover:border-white/20 hover:shadow-lg hover:-translate-y-0.5"
                      }
                    `}
                    data-testid={`access-user-selector-${u.id}`}
                  >
                    <div className="flex items-start gap-3.5">
                      {/* Avatar with presence indicator */}
                      <div className="relative flex-shrink-0">
                        <div className={`
                          w-12 h-12 rounded-xl flex items-center justify-center font-bold text-sm transition-all
                          ${isSelected 
                            ? "bg-gradient-to-br from-indigo-500 to-purple-600 text-white ring-2 ring-indigo-400/50 shadow-lg shadow-indigo-500/30" 
                            : "bg-gradient-to-br from-slate-600 to-slate-700 text-slate-200 group-hover:from-slate-500 group-hover:to-slate-600"
                          }
                        `}>
                          {u.name?.slice(0, 2).toUpperCase() || "??"}
                        </div>
                        {/* Presence indicator */}
                        <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-emerald-500 rounded-full border-2 border-slate-900 shadow-sm" />
                      </div>

                      <div className="min-w-0 flex-1 space-y-1.5">
                        <div className="font-semibold text-white truncate">{u.name}</div>
                        <div className="text-xs text-slate-400 truncate">{u.email}</div>
                        <div className="pt-0.5">
                          <RoleBadge role={u.membership_role || "unknown"} />
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* RIGHT CARD: ACCESS CONTROL CENTER (Premium Glass Card) */}
        <Card className="lg:col-span-8 bg-gradient-to-br from-white/10 to-white/5 border-white/20 shadow-2xl shadow-black/20">
          <CardHeader className="pb-4 border-b border-white/10">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg font-semibold text-white">Access Control Center</CardTitle>
                <div className="text-sm text-slate-400 mt-1">
                  Manage module permissions and access levels
                </div>
              </div>

              {/* Access Legend - Refined */}
              <div className="flex items-center gap-4 text-xs bg-white/5 px-4 py-2.5 rounded-xl border border-white/10">
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-slate-600 ring-2 ring-slate-600/30" />
                  <span className="text-slate-300 font-medium">Off</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-amber-500 ring-2 ring-amber-500/30" />
                  <span className="text-slate-300 font-medium">Read</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-orange-500 ring-2 ring-orange-500/30" />
                  <span className="text-slate-300 font-medium">Write</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-indigo-500 ring-2 ring-indigo-500/30" />
                  <span className="text-slate-300 font-medium">Full</span>
                </div>
              </div>
            </div>
          </CardHeader>

          <CardContent className="pt-6 space-y-6">
            {selectedUser ? (
              <>
                {/* User Summary Identity Card - Enhanced */}
                <div className="bg-gradient-to-br from-white/10 to-white/5 border border-white/10 rounded-2xl p-6 shadow-lg">
                  <div className="flex items-start justify-between gap-6">
                    <div className="flex items-center gap-5">
                      {/* Large Avatar */}
                      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold text-xl ring-4 ring-indigo-400/30 shadow-xl shadow-indigo-500/30">
                        {selectedUser.name?.slice(0, 2).toUpperCase() || "??"}
                      </div>

                      <div className="space-y-2">
                        <div>
                          <div className="text-xl font-bold text-white">
                            {selectedUser.name}
                          </div>
                          <div className="text-sm text-slate-400">{selectedUser.email}</div>
                        </div>
                        <RoleBadge role={selectedUser.membership_role || "unknown"} />
                      </div>
                    </div>

                    {/* Metadata Grid */}
                    <div className="grid grid-cols-2 gap-6 text-sm">
                      <div className="bg-white/5 px-4 py-2.5 rounded-xl border border-white/10">
                        <div className="text-xs text-slate-500 mb-1 font-medium">Member Since</div>
                        <div className="text-slate-200 font-semibold">
                          {selectedUser.created_at 
                            ? new Date(selectedUser.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                            : "—"}
                        </div>
                      </div>
                      <div className="bg-white/5 px-4 py-2.5 rounded-xl border border-white/10">
                        <div className="text-xs text-slate-500 mb-1 font-medium">Last Updated</div>
                        <div className="text-slate-200 font-semibold">
                          {selectedUser.updated_at 
                            ? new Date(selectedUser.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                            : "—"}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* MA-UI-154: Owner Full Access Notice */}
                {selectedUser.membership_role === "owner" && (
                  <div className="bg-indigo-500/10 border border-indigo-400/30 rounded-xl p-4 flex items-start gap-3">
                    <Shield className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="text-sm font-semibold text-indigo-200">Account Owner - Full Access</div>
                      <div className="text-xs text-slate-400 mt-1">
                        Account owners have automatic full access to all modules and cannot be modified. This ensures account security and administrative control.
                      </div>
                    </div>
                  </div>
                )}

                {loading ? (
                  <div className="flex items-center justify-center py-20">
                    <RefreshCw className="w-8 h-8 text-indigo-400 animate-spin" />
                  </div>
                ) : (
                  <>
                    {/* Module Groups - Premium Structure */}
                    <div className="space-y-3">
                      {Object.entries(MODULE_GROUPS).map(([groupKey, group]) => {
                        const isCollapsed = collapsedGroups[groupKey];
                        const GroupIcon = group.icon;

                        return (
                          <div
                            key={groupKey}
                            className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden transition-all hover:border-white/20"
                            data-testid={`module-group-${groupKey}`}
                          >
                            {/* Group Header (Collapsible) */}
                            <button
                              onClick={() => toggleGroup(groupKey)}
                              className="w-full px-6 py-4 flex items-center justify-between hover:bg-white/5 transition-colors group"
                            >
                              <div className="flex items-center gap-4">
                                <div className={`w-11 h-11 rounded-xl bg-gradient-to-br from-${group.color}-500/20 to-${group.color}-600/10 flex items-center justify-center ring-2 ring-${group.color}-500/20`}>
                                  <GroupIcon className={`w-5 h-5 text-${group.color}-400`} />
                                </div>
                                <div className="text-left">
                                  <div className="text-base font-bold text-white group-hover:text-indigo-200 transition-colors">{group.label}</div>
                                  <div className="text-xs text-slate-500 font-medium">{group.description}</div>
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                <div className="text-xs text-slate-500 font-medium">
                                  {group.modules.length} module{group.modules.length !== 1 ? 's' : ''}
                                </div>
                                {isCollapsed ? (
                                  <ChevronDown className="w-5 h-5 text-slate-400 transition-transform" />
                                ) : (
                                  <ChevronUp className="w-5 h-5 text-slate-400 transition-transform" />
                                )}
                              </div>
                            </button>

                            {/* Group Content */}
                            {!isCollapsed && (
                              <div className="px-6 pb-5 space-y-1 border-t border-white/10 pt-5">
                                {group.modules.map((moduleName, idx) => {
                                  const validLevels = MODULE_ACCESS_LEVELS[moduleName] || ["off", "full"];
                                  const isOwner = selectedUser?.membership_role === "owner";
                                  // P4-WP-7C.2: inheriting means NO explicit level is selected;
                                  // the Inherit control is selected instead.
                                  const inheriting = isInherited(moduleOverrides, moduleName);
                                  const currentLevel = inheriting ? null : moduleOverrides[moduleName];
                                  const shownLevel = displayLevel(moduleView, moduleOverrides, moduleName);
                                  const planBlocked = isBlockedByPlan(moduleView, moduleOverrides, moduleName);
                                  const governanceBlocked = isBlockedByGovernance(moduleView, moduleOverrides, moduleName);

                                  return (
                                    <div
                                      key={moduleName}
                                      className={`
                                        flex items-center justify-between py-3.5 px-4 rounded-xl
                                        transition-all hover:bg-white/5
                                        ${idx !== group.modules.length - 1 ? 'border-b border-white/5' : ''}
                                      `}
                                      data-testid={`module-control-${moduleName}`}
                                    >
                                      {/* Module Identity */}
                                      <div className="flex-1">
                                        <div className="text-sm font-semibold text-slate-100">
                                          {MODULE_LABELS[moduleName] || moduleName}
                                        </div>
                                        {/* P4-WP-7C.2: say what access actually IS, and where it
                                            comes from. The old editor showed "Off" for every
                                            inherited module, which was simply untrue. */}
                                        <div
                                          className="text-xs text-slate-400 mt-0.5"
                                          data-testid={`module-source-${moduleName}`}
                                        >
                                          {inheriting ? (
                                            <span>
                                              Inherit
                                              {shownLevel ? ` (${MODULE_LEVEL_LABELS[shownLevel] || shownLevel})` : ""}
                                            </span>
                                          ) : (
                                            <span className="text-indigo-300">
                                              Explicit
                                              {shownLevel ? ` (${MODULE_LEVEL_LABELS[shownLevel] || shownLevel})` : ""}
                                            </span>
                                          )}
                                          {governanceBlocked && (
                                            <span
                                              className="ml-2 text-rose-400"
                                              data-testid={`module-governance-blocked-${moduleName}`}
                                            >
                                              Not available for this role &mdash; not in effect
                                            </span>
                                          )}
                                          {planBlocked && (
                                            <span
                                              className="ml-2 text-amber-400"
                                              data-testid={`module-plan-blocked-${moduleName}`}
                                            >
                                              Blocked by plan &mdash; not in effect
                                            </span>
                                          )}
                                        </div>
                                      </div>

                                      {/* MA-UI-149: PREMIUM ACCESS CONTROLS (TOP PRIORITY) */}
                                      <div className="flex items-center gap-1 bg-slate-900/60 backdrop-blur-sm p-1 rounded-xl border border-white/10 shadow-inner">
                                        {/* P4-WP-7C.2: Inherit is a first-class choice, and the
                                            only way to REMOVE an explicit override. */}
                                        <button
                                          key="inherit"
                                          onClick={() => !isOwner && handleModuleInherit(moduleName)}
                                          disabled={isOwner}
                                          data-testid={`module-level-${moduleName}-inherit`}
                                          aria-pressed={inheriting}
                                          className={`
                                            px-5 py-2.5 rounded-lg text-xs font-bold uppercase tracking-wide
                                            transition-all duration-200 ease-out
                                            ${inheriting
                                              ? "bg-gradient-to-br from-slate-600 to-slate-700 text-white shadow-lg shadow-slate-600/40 ring-2 ring-slate-500/50"
                                              : "text-slate-500 hover:text-slate-300 hover:bg-white/5"}
                                            ${isOwner ? "opacity-50 cursor-not-allowed" : ""}
                                          `}
                                        >
                                          Inherit
                                        </button>
                                        {validLevels.map((level) => {
                                          const isActive = currentLevel === level;
                                          
                                          // Premium color system with glow
                                          const levelStyles = {
                                            off: {
                                              active: "bg-gradient-to-br from-slate-700 to-slate-800 text-white shadow-lg shadow-slate-700/50 ring-2 ring-slate-600/50",
                                              inactive: "text-slate-500 hover:text-slate-300 hover:bg-white/5"
                                            },
                                            read: {
                                              active: "bg-gradient-to-br from-amber-600 to-amber-700 text-white shadow-lg shadow-amber-600/40 ring-2 ring-amber-500/50",
                                              inactive: "text-slate-500 hover:text-amber-400 hover:bg-amber-500/10"
                                            },
                                            write: {
                                              active: "bg-gradient-to-br from-orange-600 to-orange-700 text-white shadow-lg shadow-orange-600/40 ring-2 ring-orange-500/50",
                                              inactive: "text-slate-500 hover:text-orange-400 hover:bg-orange-500/10"
                                            },
                                            full: {
                                              active: "bg-gradient-to-br from-indigo-600 to-indigo-700 text-white shadow-lg shadow-indigo-600/40 ring-2 ring-indigo-500/50",
                                              inactive: "text-slate-500 hover:text-indigo-400 hover:bg-indigo-500/10"
                                            }
                                          };

                                          const styles = levelStyles[level] || levelStyles.off;

                                          return (
                                            <button
                                              key={level}
                                              onClick={() => !isOwner && handleModuleChange(moduleName, level)}
                                              disabled={isOwner}
                                              className={`
                                                px-5 py-2.5 rounded-lg text-xs font-bold uppercase tracking-wide
                                                transition-all duration-200 ease-out
                                                ${isActive ? styles.active : styles.inactive}
                                                ${isActive ? 'transform scale-105' : 'hover:scale-102'}
                                                ${isOwner ? 'opacity-60 cursor-not-allowed' : ''}
                                              `}
                                              data-testid={`module-btn-${moduleName}-${level}`}
                                            >
                                              {level === "off" ? "Off" : level === "read" ? "Read" : level === "write" ? "Write" : "Full"}
                                            </button>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-24 text-slate-400">
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-slate-700/30 to-slate-800/30 flex items-center justify-center mb-5 ring-2 ring-slate-700/30">
                  <Users className="w-10 h-10 opacity-40" />
                </div>
                <div className="text-lg font-semibold text-slate-300">Select a team member</div>
                <div className="text-sm text-slate-500 mt-2">Choose someone from the directory to manage their permissions</div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* MA-UI-150: Premium Glass Save Dock (Replaces Orange Warning Bar) */}
      {/* MA-UI-154: Hide save bar for Owners (they can't be modified) */}
      {hasChanges && selectedUser?.membership_role !== "owner" && (
        <div className="fixed bottom-0 left-0 right-0 z-50 animate-in slide-in-from-bottom duration-300">
          <div className="mx-auto max-w-7xl px-8 py-4 bg-[#101828]/90 backdrop-blur-xl border-t border-white/10 shadow-2xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {/* Subtle status indicator (NOT warning orange) */}
                <div className="relative flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse ring-4 ring-indigo-500/20" />
                  <div>
                    <div className="text-sm font-semibold text-white">Unsaved changes</div>
                    <div className="text-xs text-slate-400">{changesCount} module{changesCount !== 1 ? 's' : ''} modified</div>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                {/* Reset Button - Ghost/Subtle Secondary */}
                <Button
                  onClick={handleReset}
                  variant="ghost"
                  className="text-slate-300 hover:text-white hover:bg-white/10 transition-all"
                  data-testid="reset-changes-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Reset Changes
                </Button>
                
                {/* MA-UI-152: Save Button - Match Standard MissionAssist Primary CTA */}
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  data-testid="save-module-permissions-btn"
                >
                  {saving ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Valid tab values for Admin Console - used for URL param validation
const VALID_ADMIN_TABS = [
  "users", "access", "scripts", "workspaces", "ai", "lead-sources",
  "inbound-setup", "outbound-setup", "calendars", "vocabulary", "mapping", "analytics", "diagnostics", "compliance", "quarantine"
];

export default function AdminConsolePage() {
  usePageTitle("Admin");
  const navigate = useNavigate();
  const user = getStoredUser();
  
  // P1 URL TABS: Read and sync tab state with URL query parameter
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Initialize activeTab from URL, defaulting to "users" if invalid or missing
  const [activeTab, setActiveTab] = useState(() => {
    const tabFromUrl = searchParams.get("tab");
    return tabFromUrl && VALID_ADMIN_TABS.includes(tabFromUrl) ? tabFromUrl : "users";
  });
  
  // P1 URL TABS: Sync URL when tab changes (without page reload)
  const handleTabChange = useCallback((newTab) => {
    setActiveTab(newTab);
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (newTab === "users") {
        // Default tab - remove from URL to keep it clean
        params.delete("tab");
      } else {
        params.set("tab", newTab);
      }
      return params;
    }, { replace: true });
  }, [setSearchParams]);
  
  // P0 TENANT ISOLATION: Use global TenantContext for URL-backed tenant state
  // MA-TENANTCONTEXT-ACCOUNT-METADATA-DUPLICATE-FETCH-FIX-002: Also get accounts and currentAccountName
  const { 
    selectedTenantId, 
    setSelectedTenantId, 
    isViewingOtherTenant, 
    isSuperAdmin: isSuperAdminFromContext, 
    homeTenantId,
    accounts: contextAccounts,
    currentAccountName: tenantContextAccountName
  } = useTenant();

  // MA-PHASE11A-P0-AUDIT-001: Use canonical permission hooks for all tabs
  const { hasAccess: canManageTeam } = useHasModule("admin");
  const { hasAccess: hasSettingsAccess } = useHasModule("settings");
  const { hasAccess: hasAnalyticsAccess } = useHasModule("war_room");
  const { isSuperAdmin } = useIsSuperAdmin();
  const { userRole } = usePermissions();

  // MA-PHASE11A: isSuper = SuperAdmin platform role, isAdmin = account owner/admin
  const isSuper = isSuperAdmin;
  const isAdmin = userRole === "owner" || userRole === "admin";

  // MA-PHASE11A-AUDIT-001: Canonical accounts list (replaces legacy teams)
  // MA-TENANTCONTEXT-ACCOUNT-METADATA-DUPLICATE-FETCH-FIX-002: Use accounts from TenantContext, no fetch
  const canonicalAccounts = useMemo(() => {
    if (!isSuper) return [];
    return contextAccounts.map(acc => ({
      id: acc.account_id,
      name: acc.account_name
    }));
  }, [isSuper, contextAccounts]);

  // P0: Prevent toast spam + retry loop
  const refreshInFlight = useRef(false);
  const lastRefreshErrorAt = useRef(0);
  const refreshRetryCount = useRef(0);
  const MAX_REFRESH_RETRIES = 3;
  const shownErrorIds = useRef(new Set()); // Track unique errors shown this session
  
  // P0: Track if initial load has run to prevent infinite re-renders
  const initialLoadDone = useRef(false);

  // P0: Data load error state for inline error banner
  const [dataLoadError, setDataLoadError] = useState(null);

  // MA-PHASE11A-LEGACY-CONTROLS-AUDIT-001: Removed Super Admin Controls state
  // - createTeamOpen, teamName (LEGACY - use Manage Accounts page - REMOVED)
  
  // MA-PHASE11A-TEAM-MEMBERS-FIX-001: Invite state (used by Team Members tab)
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  // P4-WP-8C.6: inviter-supplied identity metadata for the pending invitation.
  const [inviteFirstName, setInviteFirstName] = useState("");
  const [inviteLastName, setInviteLastName] = useState("");
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  // Set only when creation succeeded but delivery did not, so the admin can
  // hand the link over manually. Cleared on every new attempt.
  const [inviteFallbackUrl, setInviteFallbackUrl] = useState(null);
  const [inviteRole, setInviteRole] = useState(DEFAULT_INVITE_ROLE);  // P4-WP-8C.3: canonical, never legacy
  const [inviteWorkspaces, setInviteWorkspaces] = useState([]);

  // P4-WP-8C.5: pending invitations are a separate projection from `users`.
  // They are membership-lifecycle objects with no user_id and no `db.users`
  // row, so they are deliberately NOT merged into `users`.
  const [pendingInvites, setPendingInvites] = useState([]);
  const [pendingInvitesError, setPendingInvitesError] = useState(null);
  const [cancellingInviteId, setCancellingInviteId] = useState(null);

  const [users, setUsers] = useState([]);
  const [scripts, setScripts] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [availableWorkspaces, setAvailableWorkspaces] = useState([]);

  // Delete user
  const [deleteUserOpen, setDeleteUserOpen] = useState(false);
  const [deleteUser, setDeleteUser] = useState(null);

  // Access grants
  const [accessUserId, setAccessUserId] = useState(null);
  const [accessLoading, setAccessLoading] = useState(false);
  const [ptViewIds, setPtViewIds] = useState({});
  const [ptEditIds, setPtEditIds] = useState({});
  const [scriptViewIds, setScriptViewIds] = useState({});
  const [scriptEditIds, setScriptEditIds] = useState({});

  // Script copy (super admin)
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyFromScriptId, setCopyFromScriptId] = useState(null);
  const [copyToTeamId, setCopyToTeamId] = useState(null);
  const [copyToProductTypeId, setCopyToProductTypeId] = useState(null);
  const [copyToScriptTypeId, setCopyToScriptTypeId] = useState(null);
  const [copyName, setCopyName] = useState("");
  const [copyTargetProductTypes, setCopyTargetProductTypes] = useState([]);
  const [copyTargetScriptTypes, setCopyTargetScriptTypes] = useState([]);
  const [copyBusy, setCopyBusy] = useState(false);

  // AI Providers (BYOK LLM keys)
  const [llmStatus, setLlmStatus] = useState({ openai: false, anthropic: false });
  const [llmProvider, setLlmProvider] = useState("openai");
  const [llmModel, setLlmModel] = useState("gpt-5.1");
  const [llmApiKey, setLlmApiKey] = useState("");
  const [llmBusy, setLlmBusy] = useState(false);
  const [llmTestBusy, setLlmTestBusy] = useState(false);
  const [llmTestResult, setLlmTestResult] = useState(null);


  const canGrantEditToRole = (targetRole) => targetRole === "admin" || targetRole === "super_admin";


  const fetchLlmStatus = useCallback(async () => {
    try {
      const params = isSuper && selectedTenantId ? { tenant_id: selectedTenantId } : {};
      const res = await api.get("/llm-keys", { params });
      const st = { openai: false, anthropic: false };
      (res.data || []).forEach((row) => {
        if (row.provider === "openai") st.openai = !!row.connected;
        if (row.provider === "anthropic") st.anthropic = !!row.connected;
      });
      setLlmStatus(st);
    } catch (e) {
      // silent
    }
  }, [isSuper, selectedTenantId]);

  const fetchWorkspaces = useCallback(async (tenantIdOverride = null) => {
    try {
      const effectiveTenantId = tenantIdOverride || selectedTenantId;
      const params = isSuper && effectiveTenantId ? { tenant_id: effectiveTenantId } : {};
      const res = await api.get("/tenants/workspaces/all", { params });
      setAvailableWorkspaces(res.data || []);
    } catch (e) {
      // silent - workspaces may not be available in legacy tenants
    }
  }, [isSuper, selectedTenantId]);

  // MA-PHASE11A-AUDIT-001: Removed loadTeamsOnce() - no longer fetching legacy /tenants endpoint

  const refresh = useCallback(async (teamIdOverride = null, isRetry = false) => {
    if (refreshInFlight.current) return;
    
    // P0: Prevent infinite retry loops
    if (isRetry) {
      refreshRetryCount.current += 1;
      if (refreshRetryCount.current > MAX_REFRESH_RETRIES) {
        setDataLoadError({
          message: "Failed to load admin data after multiple attempts",
          detail: "Please check your connection and try again.",
          canRetry: true,
        });
        return;
      }
    } else {
      // Reset retry count on fresh load
      refreshRetryCount.current = 0;
    }
    
    refreshInFlight.current = true;
    setDataLoadError(null); // Clear previous errors

    try {
      const effectiveTeamId = isSuper ? (teamIdOverride || selectedTenantId) : null;
      const teamParams = isSuper ? { tenant_id: effectiveTeamId || undefined } : {};

      // MA-PHASE11A5-ADMIN-ROLE-AND-403-FIX-001: Load users only (lazy load other tabs)
      const u = await api.get("/admin/users", { params: teamParams });
      setUsers(u.data || []);
      setAccessUserId((prev) => prev || u.data?.[0]?.id || null);

      // When switching teams, ensure we select a user from the newly loaded team.
      if (isSuper && teamIdOverride) {
        setAccessUserId(u.data?.[0]?.id || null);
      }

      // P4-WP-8C.5: load the governed pending-invitation projection.
      //
      // Deliberately tolerated separately from the members load. This route
      // requires `admin` at min_level="full" while `/admin/users` requires only
      // `admin`, so an administrator with the lower level must still get a
      // working members list rather than a blank console. A 403 here means
      // "not authorized to see invitations", which is an empty list, not an
      // error to shout about; anything else is reported inline.
      try {
        const p = await api.get("/admin/members/pending");
        setPendingInvites(Array.isArray(p.data) ? p.data : []);
        setPendingInvitesError(null);
      } catch (pe) {
        setPendingInvites([]);
        setPendingInvitesError(
          pe?.response?.status === 403 ? null : "Pending invitations could not be loaded."
        );
      }

      // Scripts and product types will be loaded when their tabs are accessed
      // (see loadScripts and loadProductTypes functions)
      
      // Success - reset retry count
      refreshRetryCount.current = 0;
    } catch (e) {
      const errorId = `${e?.response?.status || 'network'}_${e?.response?.config?.url || 'unknown'}`;
      const now = Date.now();
      
      // P0: Only show toast ONCE per unique error type per session
      // Also respect 5-second debounce for repeated failures
      if (!shownErrorIds.current.has(errorId) && (now - lastRefreshErrorAt.current > 5000)) {
        toast({
          title: "Admin data failed to load",
          description: e?.response?.data?.detail || "Please try again.",
          variant: "destructive",
        });
        shownErrorIds.current.add(errorId);
        lastRefreshErrorAt.current = now;
      }
      
      // P0: Set inline error state for retry banner
      setDataLoadError({
        message: "Failed to load admin data",
        detail: e?.response?.data?.detail || "Please check your connection.",
        canRetry: refreshRetryCount.current < MAX_REFRESH_RETRIES,
        endpoint: e?.response?.config?.url,
        status: e?.response?.status,
      });
    } finally {
      refreshInFlight.current = false;
    }
  }, [isSuper, selectedTenantId]);

  // MA-PHASE11A5: Lazy load scripts when Scripts tab is accessed
  const loadScripts = useCallback(async () => {
    if (scripts.length > 0) return; // Already loaded
    
    try {
      const effectiveTeamId = isSuper ? selectedTenantId : null;
      const teamParams = isSuper ? { tenant_id: effectiveTeamId || undefined } : {};
      const s = await api.get("/admin/scripts", { params: teamParams });
      setScripts(s.data || []);
    } catch (e) {
      console.error('[AdminConsole] Failed to load scripts:', e);
      // Scoped error - don't break the whole page
    }
  }, [isSuper, selectedTenantId, scripts.length]);

  // MA-PHASE11A5: Lazy load product types when needed
  const loadProductTypes = useCallback(async () => {
    if (productTypes.length > 0) return; // Already loaded
    
    try {
      const effectiveTeamId = isSuper ? selectedTenantId : null;
      const teamParams = isSuper ? { tenant_id: effectiveTeamId || undefined } : {};
      const pt = await api.get("/product-types", { params: teamParams });
      setProductTypes(pt.data || []);
    } catch (e) {
      console.error('[AdminConsole] Failed to load product types:', e);
      // Scoped error - don't break the whole page
    }
  }, [isSuper, selectedTenantId, productTypes.length]);

  useEffect(() => {
    // P0 FIX: Prevent infinite re-render loop caused by getStoredUser() creating
    // new object references on every render. Only run initial load once.
    if (initialLoadDone.current) return;
    
    if (!user || !(isSuper || isAdmin)) {
      navigate("/app", { replace: true });
      return;
    }

    // Mark as done BEFORE starting async work to prevent race conditions
    initialLoadDone.current = true;

    // MA-PHASE11A-AUDIT-001: Initial load now uses TenantContext.selectedTenantId (canonical)
    // No longer fetching legacy /tenants endpoint
    const init = async () => {
      refresh(selectedTenantId);
      // MA-LLM-SECURITY-FIX: Only fetch LLM status for SuperAdmins
      if (isSuper) {
        await fetchLlmStatus();
      }
      await fetchWorkspaces();
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, isSuper, isAdmin, navigate, selectedTenantId]);

  // MA-PHASE11A5: Lazy load scripts when Scripts tab becomes active
  useEffect(() => {
    if (activeTab === 'scripts') {
      loadScripts();
    }
  }, [activeTab, loadScripts]);

  // MA-PHASE11A-AUDIT-001: Removed handleTenantSwitch, confirmTenantSwitch, cancelTenantSwitch
  // Account switching is now handled exclusively by Phase 11A Account Switcher in left rail

  // MA-TENANTCONTEXT-ACCOUNT-METADATA-DUPLICATE-FETCH-FIX-002: Use currentAccountName from TenantContext
  const currentAccountName = tenantContextAccountName || selectedTenantId;
  
  // For backwards compatibility, alias currentAccountName as currentTenantName
  const currentTenantName = currentAccountName;

  // Check if viewing a different tenant than user's own - now provided by TenantContext
  // const isViewingOtherTenant is already provided by useTenant()

  const accessUser = useMemo(() => users.find((u) => u.id === accessUserId) || null, [users, accessUserId]);

  const loadAccess = useCallback(
    async (uid) => {
      if (!uid) return;
      setAccessLoading(true);
      try {
        const res = await api.get(`/admin/users/${uid}/access`);

        const nextPtView = {};
        const nextPtEdit = {};
        for (const g of res.data?.product_types || []) {
          nextPtView[g.product_type_id] = true;
          if (g.can_edit) nextPtEdit[g.product_type_id] = true;
        }

        const nextSView = {};
        const nextSEdit = {};
        for (const g of res.data?.scripts || []) {
          nextSView[g.script_id] = true;
          if (g.can_edit) nextSEdit[g.script_id] = true;
        }

        setPtViewIds(nextPtView);
        setPtEditIds(nextPtEdit);
        setScriptViewIds(nextSView);
        setScriptEditIds(nextSEdit);
      } catch (e) {
        toast({
          title: "Could not load access",
          description: e?.response?.data?.detail || "Please try again.",
          variant: "destructive",
        });
      } finally {
        setAccessLoading(false);
      }
    },
    [setAccessLoading]
  );

  useEffect(() => {
    loadAccess(accessUserId);
  }, [accessUserId, loadAccess]);

  // P4-WP-8C.3: reset to the canonical default, never to a legacy value.
  // The previous resets set `agent`, so the second invitation of any session
  // started on a role no write path may produce.
  const resetInviteForm = () => {
    setInviteOpen(false);
    setInviteEmail("");
    setInviteFirstName("");
    setInviteLastName("");
    setInviteRole(resolveInviteRole(userRole, DEFAULT_INVITE_ROLE));
    setInviteWorkspaces([]);
  };

  const toggleInviteWorkspace = (workspaceId) => {
    setInviteWorkspaces((prev) =>
      prev.includes(workspaceId)
        ? prev.filter((id) => id !== workspaceId)
        : [...prev, workspaceId]
    );
  };

  // P4-WP-8C.3: this posted to `/invites` -- a route deleted long enough ago
  // that auth.py documents the deletion -- so every invitation 404'd. It now
  // targets the governed Account membership surface.
  const onInvite = async () => {
    setInviteSubmitting(true);
    setInviteFallbackUrl(null);
    try {
      const res = await api.post("/admin/members/invite", {
        email: inviteEmail,
        role: resolveInviteRole(userRole, inviteRole),
        first_name: inviteFirstName.trim() || undefined,
        last_name: inviteLastName.trim() || undefined,
        workspace_ids: inviteWorkspaces.length > 0 ? inviteWorkspaces : undefined,
        default_workspace_id: inviteWorkspaces.length > 0 ? inviteWorkspaces[0] : undefined,
      });

      // P4-WP-8C.6: persistence and delivery are distinct outcomes, so the
      // confirmation reports which one happened. Reaching here at all means the
      // invitation EXISTS -- a failed send is not a failed invitation. Saying
      // "failed" would send the admin looking to create it again, when it is
      // already there and already occupying the uniqueness slot.
      const invited = [inviteFirstName.trim(), inviteLastName.trim()]
        .filter(Boolean)
        .join(" ");
      const who = invited || inviteEmail.trim();

      if (res.data?.invite_sent) {
        toast({ title: `Invitation sent to ${who}` });
        resetInviteForm();
      } else {
        // Keep the dialog's fallback link available; the invitation is valid
        // and will show as Pending either way.
        setInviteFallbackUrl(res.data?.invite_url || null);
        toast({
          title: "Invitation created, but the email could not be sent",
          description: "Copy the invitation link below and send it to them directly.",
        });
        resetInviteForm();
      }
      // Pessimistic: only refresh once the server has confirmed.
      await refresh();
    } catch (e) {
      toast({
        title: "Invite failed",
        description: removalErrorMessage(e),
        variant: "destructive",
      });
    } finally {
      setInviteSubmitting(false);
    }
  };

  const onCreateTeam = async () => {
    try {
      const res = await api.post("/tenants", { name: teamName });
      toast({ title: "Account created" });
      setCreateTeamOpen(false);
      setTeamName("");
      await refresh();
      setSelectedTeamId(res.data?.id || null);
    } catch (e) {
      toast({
        title: "Create account failed",
        description: e?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    }
  };

  const onSaveLlmKey = async () => {
    if (!llmApiKey.trim()) {
      toast({ title: "API key required", variant: "destructive" });
      return;
    }

    setLlmBusy(true);
    try {
      const payload = { provider: llmProvider, api_key: llmApiKey.trim() };
      if (isSuper && selectedTenantId) {
        payload.tenant_id = selectedTenantId;
      }
      await api.post("/llm-keys", payload);
      toast({ title: "Key saved" });
      setLlmApiKey("");
      await fetchLlmStatus();
    } catch (e) {
      toast({
        title: "Save failed",
        description: e?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setLlmBusy(false);
    }
  };

  const onDisconnectLlmKey = async (provider) => {
    setLlmBusy(true);
    try {
      const params = isSuper && selectedTenantId ? { tenant_id: selectedTenantId } : {};
      await api.delete(`/llm-keys/${provider}`, { params });
      toast({ title: "Disconnected" });
      await fetchLlmStatus();
    } catch (e) {
      toast({
        title: "Disconnect failed",
        description: e?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setLlmBusy(false);
    }
  };

  const onTestLlmKey = async () => {
    setLlmTestBusy(true);
    setLlmTestResult(null);
    try {
      const payload = {
        provider: llmProvider,
        model: llmModel,
        api_key: llmApiKey.trim() ? llmApiKey.trim() : undefined,
      };
      if (isSuper && selectedTenantId) {
        payload.tenant_id = selectedTenantId;
      }
      const res = await api.post("/llm-keys/test", payload);
      setLlmTestResult(res.data?.result || res.data);
      toast({ title: "Test succeeded" });
    } catch (e) {
      toast({
        title: "Test failed",
        description: e?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setLlmTestBusy(false);
    }
  };

  const onUpdateUser = async (u, patch) => {
    try {
      await api.put(`/admin/users/${u.id}`, patch);
      toast({ title: "User updated" });
      await refresh();
    } catch (e) {
      toast({
        title: "Update failed",
        description: e?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    }
  };

  // P4-WP-8B.2: removal is ACCOUNT MEMBERSHIP revocation, not identity deletion.
  const canRemoveMember = (u) => {
    if (!u) return false;
    if (!(isSuper || isAdmin)) return false;
    // Self-removal is permitted by the API but deliberately not offered here;
    // a "Leave this Account" flow is its own design, not a governance package.
    if (u.id === user?.id) return false;
    // MA-PHASE11A5: Check platform_role, not legacy role
    if (isAdmin && u.platform_role === "super_admin") return false;
    // Only an Owner may remove another Owner. The backend refuses this too --
    // this just stops offering an action that would always be denied.
    if (u.membership_role === "owner" && userRole !== "owner") return false;
    return true;
  };

  // P4-WP-8B.2: bounded, specific copy per governance outcome. None of these
  // retries automatically -- least of all the uncertain one, where the change
  // may already have been applied.
  const removalErrorMessage = (e) => {
    const status = e?.response?.status;
    const detail = e?.response?.data?.detail;
    if (detail && typeof detail === "object") {
      if (detail.completion === "uncertain") {
        return (
          "The membership change may have been applied, but MissionAssist " +
          "could not finalize the governance audit. Refresh before retrying."
        );
      }
      if (detail.message) return detail.message;
    }
    if (status === 503) {
      return (
        "The change was not applied because governance evidence could not " +
        "be recorded. Try again later."
      );
    }
    if (typeof detail === "string" && detail) return detail;
    return "Please try again.";
  };

  // P4-WP-8C.5: invoke WP-8C.4's existing cancellation endpoint. The frontend
  // deliberately owns NO cancellation semantics of its own -- no second API, no
  // local status rewrite. The row disappears because the authoritative read
  // surface stops returning it, never because the UI decided it should.
  const onCancelInvite = async (membershipId) => {
    if (!membershipId) return;
    setCancellingInviteId(membershipId);
    try {
      await api.delete(`/admin/members/pending/${membershipId}`);
      toast({ title: "Invitation cancelled" });
      // Pessimistic: the list is re-read, never optimistically spliced. A
      // failure below must not leave the row visually gone.
      await refresh();
    } catch (e) {
      toast({
        title: "Cancel invitation failed",
        description: removalErrorMessage(e),
        variant: "destructive",
      });
    } finally {
      setCancellingInviteId(null);
    }
  };

  // P4-WP-8C: Account role lives on the MEMBERSHIP. The old dropdown wrote
  // db.users.role -- a global field no authorization path reads -- so it
  // appeared to assign a role and changed nothing at all.
  const onChangeRole = async (u, role) => {
    try {
      await api.patch(`/admin/members/${u.id}/role`, { role });
      toast({ title: "Role updated" });
      await refresh();
    } catch (e) {
      toast({
        title: "Role change failed",
        description: removalErrorMessage(e),
        variant: "destructive",
      });
    }
  };

  // P4-WP-8C: replaces "Disable", which wrote db.users.enabled and locked the
  // person out of MissionAssist ENTIRELY -- every Account, not just this one.
  // Suspension is Account-scoped and reversible.
  const onChangeStatus = async (u, status, successTitle) => {
    try {
      await api.patch(`/admin/members/${u.id}/status`, { status });
      toast({ title: successTitle });
      await refresh();
    } catch (e) {
      toast({
        title: "Status change failed",
        description: removalErrorMessage(e),
        variant: "destructive",
      });
    }
  };

  // Only an Owner may administer an Owner -- role or lifecycle alike. Mirrors
  // the backend, which stays authoritative; this only stops offering actions
  // that would always be refused.
  const canAdministerMember = (u) => {
    if (!u) return false;
    if (!(isSuper || isAdmin)) return false;
    if (u.membership_role === "owner" && userRole !== "owner") return false;
    return true;
  };

  const onRemoveMember = async (u) => {
    try {
      await api.delete(`/admin/members/${u.id}`);
      toast({ title: "Removed from Account" });
      setDeleteUserOpen(false);
      setDeleteUser(null);
      // Pessimistic: only refresh once the server has confirmed.
      await refresh();
    } catch (e) {
      toast({
        title: "Remove failed",
        description: removalErrorMessage(e),
        variant: "destructive",
      });
    }
  };

  const buildAccessPayload = () => {
    const allowEdit = canGrantEditToRole(accessUser?.role);

    const product_types = [];
    for (const pt of productTypes) {
      if (!ptViewIds[pt.id]) continue;
      product_types.push({
        product_type_id: pt.id,
        can_edit: allowEdit ? Boolean(ptEditIds[pt.id]) : false,
      });
    }

    const scriptsPayload = [];
    for (const s of scripts) {
      if (!scriptViewIds[s.id]) continue;
      scriptsPayload.push({
        script_id: s.id,
        can_edit: allowEdit ? Boolean(scriptEditIds[s.id]) : false,
      });
    }

    return {
      product_types,
      scripts: scriptsPayload,
    };
  };

  const onSaveAccess = async () => {
    if (!accessUserId) return;
    try {
      const payload = buildAccessPayload();
      await api.put(`/admin/users/${accessUserId}/access`, payload);
      toast({ title: "Access saved" });
    } catch (e) {
      toast({
        title: "Save failed",
        description: e?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    }
  };

  const loadCopyTargetsForTeam = async (teamId) => {
    if (!teamId) return;
    const pts = await api.get("/product-types", { params: { tenant_id: teamId } });
    setCopyTargetProductTypes(pts.data || []);
    const firstPt = pts.data?.[0]?.id || null;
    setCopyToProductTypeId((prev) => prev || firstPt);

    if (firstPt) {
      const sts = await api.get(`/product-types/${firstPt}/script-types`, { params: { tenant_id: teamId } });
      setCopyTargetScriptTypes(sts.data || []);
      setCopyToScriptTypeId((prev) => prev || sts.data?.[0]?.id || null);
    } else {
      setCopyTargetScriptTypes([]);
      setCopyToScriptTypeId(null);
    }
  };

  useEffect(() => {
    if (!copyOpen) return;
    const tId = copyToTeamId || selectedTenantId;
    setCopyToTeamId(tId);
    loadCopyTargetsForTeam(tId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [copyOpen]);

  useEffect(() => {
    if (!copyOpen) return;
    if (!copyToTeamId) return;
    loadCopyTargetsForTeam(copyToTeamId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [copyToTeamId]);

  useEffect(() => {
    const run = async () => {
      if (!copyOpen) return;
      if (!copyToTeamId || !copyToProductTypeId) return;
      const sts = await api.get(`/product-types/${copyToProductTypeId}/script-types`, { params: { tenant_id: copyToTeamId } });
      setCopyTargetScriptTypes(sts.data || []);
      setCopyToScriptTypeId((prev) => prev || sts.data?.[0]?.id || null);
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [copyToProductTypeId]);

  useEffect(() => {
    if (!copyOpen) return;
    if (!copyFromScriptId) return;
    const s = scripts.find((x) => x.id === copyFromScriptId);
    if (!s) return;
    setCopyName((prev) => prev || `${s.name || "Script"} (Copy)`);
  }, [copyOpen, copyFromScriptId, scripts]);

  const onCopyScript = async () => {
    if (!copyFromScriptId || !copyToTeamId || !copyToProductTypeId || !copyToScriptTypeId) return;
    if (!copyName.trim()) return;

    setCopyBusy(true);
    try {
      await api.post(`/tenants/${copyToTeamId}/copy-script`, {
        from_script_id: copyFromScriptId,
        to_product_type_id: copyToProductTypeId,
        to_script_type_id: copyToScriptTypeId,
        name: copyName.trim(),
      });
      toast({ title: "Script copied" });
      setCopyOpen(false);
      setCopyFromScriptId(null);
      setCopyName("");
      setCopyToProductTypeId(null);
      setCopyToScriptTypeId(null);
      setCopyToTeamId(null);
      await refresh();
    } catch (e) {
      toast({
        title: "Copy failed",
        description: e?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setCopyBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-console-page">
      {/* MA-PHASE11A-AUDIT-001: Removed tenant switch confirmation dialog
          Account switching now handled by Phase 11A Account Switcher in left rail */}

      {/* P0: Cross-Tenant Context Banner */}
      {isSuper && isViewingOtherTenant && (
        <div 
          className="bg-amber-500/15 border border-amber-500/30 rounded-lg px-4 py-3 flex items-center gap-3"
          data-testid="tenant-context-banner"
        >
          <AlertTriangle className="h-5 w-5 text-amber-400 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-amber-200 font-medium text-sm">
              Viewing Account: <span className="text-amber-100">{currentTenantName}</span>
            </p>
            <p className="text-amber-300/70 text-xs">
              All admin actions will apply to this account. Your home account: {user?.tenant_id?.slice(0, 8)}...
            </p>
          </div>
          <Badge className="bg-amber-500/20 text-amber-200 ring-1 ring-amber-400/30">
            Cross-Tenant
          </Badge>
        </div>
      )}

      {/* P0: Data Load Error Banner with Retry */}
      {dataLoadError && (
        <div 
          className="bg-red-500/15 border border-red-500/30 rounded-lg px-4 py-3 flex items-center gap-3"
          data-testid="data-load-error-banner"
        >
          <AlertTriangle className="h-5 w-5 text-red-400 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-red-200 font-medium text-sm">{dataLoadError.message}</p>
            <p className="text-red-300/70 text-xs">{dataLoadError.detail}</p>
            {dataLoadError.endpoint && (
              <p className="text-red-400/50 text-xs mt-1">
                Endpoint: {dataLoadError.endpoint} {dataLoadError.status && `(${dataLoadError.status})`}
              </p>
            )}
          </div>
          {dataLoadError.canRetry && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => refresh(selectedTenantId, true)}
              className="bg-red-500/20 text-red-200 border-red-500/30 hover:bg-red-500/30"
              data-testid="retry-load-button"
            >
              <RefreshCw className="h-4 w-4 mr-1" />
              Retry
            </Button>
          )}
        </div>
      )}

      {/* MA-PHASE11A-LEGACY-CONTROLS-AUDIT-001: Removed Super Admin Controls card
          
          REMOVED FUNCTIONALITY:
          1. "Current Account" badge - REDUNDANT (left rail Account Switcher shows this)
          2. "Invite" button - DUPLICATE (use Team Members tab in Admin page)
          3. "New Account" button - LEGACY & BROKEN (wrote to db.tenants, use /app/accounts Manage Accounts page)
          
          CANONICAL REPLACEMENTS:
          - Account switching: Left rail Account Switcher ONLY
          - Account creation: /app/accounts (Manage Accounts page) → Create Sub-Account
          - User invitation: Admin page → Team Members tab → Invite button
          - Current account display: Left rail Account Switcher (expanded state)
      */}

      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full" data-testid="admin-console-tabs">
        <div className="flex items-center justify-between gap-4 mb-2">
          <TabsList
            className="inline-flex h-auto w-fit max-w-full flex-wrap justify-start gap-1 bg-indigo-500/20 text-indigo-100 ring-1 ring-indigo-400/20"
            data-testid="admin-console-tabs-list"
          >
          <TabsTrigger
            value="users"
            className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-100"
            data-testid="admin-console-tab-users"
          >
            <Users className="mr-2 h-4 w-4" /> Users
          </TabsTrigger>
          <TabsTrigger
            value="access"
            className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-100"
            data-testid="admin-console-tab-access"
          >
            <Shield className="mr-2 h-4 w-4" /> Access
          </TabsTrigger>
          
          {/* MA-PHASE11A-P0-AUDIT-001: Scripts tab - use settings module */}
          {(isSuper || hasSettingsAccess) ? (
            <TabsTrigger
              value="scripts"
              className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-100"
              data-testid="admin-console-tab-scripts"
            >
              <Copy className="mr-2 h-4 w-4" /> Scripts
            </TabsTrigger>
          ) : null}

          {/* MA-PHASE11A-P0-AUDIT-001: Workspaces tab - use settings module */}
          {(isSuper || hasSettingsAccess) ? (
            <TabsTrigger
              value="workspaces"
              className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-100"
              data-testid="admin-console-tab-workspaces"
            >
              <Layers className="mr-2 h-4 w-4" /> Workspaces
            </TabsTrigger>
          ) : null}

          {/* MA-PHASE11A-P0-AUDIT-001: AI tab - SuperAdmin only (platform-level) */}
          {isSuper ? (
            <TabsTrigger
              value="ai"
              className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-100"
              data-testid="admin-console-tab-ai"
            >
              <Sparkles className="mr-2 h-4 w-4" /> AI
            </TabsTrigger>
          ) : null}

          {/* MA-PHASE11A-P0-AUDIT-001: Lead Sources tab - use settings module */}
          {(isSuper || hasSettingsAccess) ? (
            <TabsTrigger
              value="lead-sources"
              className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-100"
              data-testid="admin-console-tab-lead-sources"
            >
              <Tag className="mr-2 h-4 w-4" /> Lead Sources
            </TabsTrigger>
          ) : null}

          {/* MA-PHASE11A-P0-AUDIT-001: Inbound Setup tab - use settings module */}
          {(isSuper || hasSettingsAccess) ? (
            <TabsTrigger
              value="inbound-setup"
              className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-100"
              data-testid="admin-console-tab-inbound-setup"
            >
              <Inbox className="mr-2 h-4 w-4" /> Inbound
            </TabsTrigger>
          ) : null}

          {/* MA-PHASE11A-P0-AUDIT-001: Outbound Setup tab - use settings module (no "outbound" module exists) */}
          {(isSuper || hasSettingsAccess) ? (
            <TabsTrigger
              value="outbound-setup"
              className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-100"
              data-testid="admin-console-tab-outbound-setup"
            >
              <Send className="mr-2 h-4 w-4" /> Outbound
            </TabsTrigger>
          ) : null}

          {/* MA-PHASE11A-P0-AUDIT-001: Calendars tab - use settings module (no "calendars" module exists) */}
          {(isSuper || hasSettingsAccess) ? (
            <TabsTrigger
              value="calendars"
              className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-100"
              data-testid="admin-console-tab-calendars"
            >
              <Calendar className="mr-2 h-4 w-4" /> Calendars
            </TabsTrigger>
          ) : null}

          {/* MA-PHASE11A-P0-AUDIT-001: Vocabulary tab - use settings module */}
          {(isSuper || hasSettingsAccess) ? (
            <TabsTrigger
              value="vocabulary"
              className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-100"
              data-testid="admin-console-tab-vocabulary"
            >
              <Hash className="mr-2 h-4 w-4" /> Vocabulary
            </TabsTrigger>
          ) : null}

          {/* MA-PHASE11A-P0-AUDIT-001: Mapping/Verticals tab - use settings module (no "verticals" module exists) */}
          {(isSuper || hasSettingsAccess) ? (
            <TabsTrigger
              value="mapping"
              className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-100"
              data-testid="admin-console-tab-mapping"
            >
              <Map className="mr-2 h-4 w-4" /> Mapping
            </TabsTrigger>
          ) : null}

          {/* MA-PHASE11A-P0-AUDIT-001: Analytics tab - use analytics module */}
          {(isSuper || hasAnalyticsAccess) ? (
            <TabsTrigger
              value="analytics"
              className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-100"
              data-testid="admin-console-tab-analytics"
            >
              <BarChart3 className="mr-2 h-4 w-4" /> Analytics
            </TabsTrigger>
          ) : null}

          {/* Diagnostics tab - SuperAdmin only (correct) */}
          {isSuper ? (
            <TabsTrigger
              value="diagnostics"
              className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-100"
              data-testid="admin-console-tab-diagnostics"
            >
              <Activity className="mr-2 h-4 w-4" /> Diagnostics
            </TabsTrigger>
          ) : null}

          {/* MA-PHASE11A-P0-AUDIT-001: Compliance tab - use settings module */}
          {(isSuper || hasSettingsAccess) ? (
            <TabsTrigger
              value="compliance"
              className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-100"
              data-testid="admin-console-tab-compliance"
            >
              <Shield className="mr-2 h-4 w-4" /> Compliance
            </TabsTrigger>
          ) : null}

          {/* MA-PHASE11A-P0-AUDIT-001: Quarantine tab - use settings module */}
          {(isSuper || hasSettingsAccess) ? (
            <TabsTrigger
              value="quarantine"
              className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-100"
              data-testid="admin-console-tab-quarantine"
            >
              <AlertOctagon className="mr-2 h-4 w-4" /> Quarantine
            </TabsTrigger>
          ) : null}
        </TabsList>

        </div>

        <TabsContent value="users" className="mt-5" data-testid="admin-console-users-tab">
          <Card className="bg-white/5 border-white/10" data-testid="admin-users-card">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-xl text-white flex items-center gap-2" data-testid="admin-users-title">
                    <Users className="h-5 w-5 text-indigo-400" />
                    Account Members
                  </CardTitle>
                  <div className="text-sm text-slate-400 mt-1">
                    Manage users, roles, and permissions for this account
                  </div>
                </div>
                
                {/* MA-PHASE11A-TEAM-MEMBERS-FIX-001: Add Invite Team Member CTA */}
                <InviteMemberDialog
                  open={inviteOpen}
                  onOpenChange={setInviteOpen}
                  actorRole={userRole}
                  title="Invite Account Member"
                  accountName={currentTenantName}
                  firstName={inviteFirstName}
                  onFirstNameChange={setInviteFirstName}
                  lastName={inviteLastName}
                  onLastNameChange={setInviteLastName}
                  submitting={inviteSubmitting}
                  email={inviteEmail}
                  onEmailChange={setInviteEmail}
                  role={inviteRole}
                  onRoleChange={setInviteRole}
                  availableWorkspaces={availableWorkspaces}
                  selectedWorkspaces={inviteWorkspaces}
                  onToggleWorkspace={toggleInviteWorkspace}
                  onSubmit={onInvite}
                  onCancel={resetInviteForm}
                  triggerLabel="Invite Account Member"
                  triggerTestId="invite-team-member-button"
                  emailTestId="invite-email-input"
                  roleTestId="invite-role-select"
                  submitTestId="invite-submit-button"
                  workspaceTestIdPrefix="invite-workspace-checkbox-"
                />
              </div>
            </CardHeader>
            <CardContent>
              {/* P4-WP-8C.5: outstanding invitations.

                  Rendered as a distinct section above the members list rather
                  than as rows inside it. A pending invitation is not a member:
                  nobody has accepted, no identity exists, and none of the
                  member actions (role change, suspend, remove) apply to it.
                  Showing it as a member row would invite exactly that mistake. */}
              {/* P4-WP-8C.6: delivery failed but the invitation is real. The
                  admin gets the same governed URL the API already returns, so
                  they can deliver it themselves. Rendered, never logged. */}
              {inviteFallbackUrl && (
                <div
                  className="mb-4 rounded-lg border border-amber-400/30 bg-amber-500/10 p-3"
                  data-testid="invite-fallback-link"
                >
                  <div className="text-sm font-medium text-amber-100">
                    Invitation created, but the email could not be sent
                  </div>
                  <p className="mt-1 text-xs text-slate-300">
                    The invitation is valid and listed below as Pending. Send this
                    link to them directly:
                  </p>
                  <div className="mt-2 flex items-center gap-2">
                    <Input
                      readOnly
                      value={inviteFallbackUrl}
                      className="bg-slate-800/60 border border-slate-600/60 text-xs text-slate-200"
                      data-testid="invite-fallback-url-input"
                      onFocus={(e) => e.target.select()}
                    />
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        navigator.clipboard?.writeText(inviteFallbackUrl);
                        toast({ title: "Invitation link copied" });
                      }}
                      data-testid="invite-fallback-copy-button"
                    >
                      Copy
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setInviteFallbackUrl(null)}
                      data-testid="invite-fallback-dismiss-button"
                    >
                      Dismiss
                    </Button>
                  </div>
                </div>
              )}

              {pendingInvitesError && (
                <div
                  className="mb-4 p-3 rounded-lg bg-amber-500/10 border border-amber-400/20 text-sm text-amber-100"
                  data-testid="admin-pending-invitations-error"
                >
                  {pendingInvitesError}
                </div>
              )}

              {pendingInvites.length > 0 && (
                <div className="mb-6" data-testid="admin-pending-invitations-section">
                  <div className="text-sm font-medium text-slate-300 mb-2">
                    Pending Invitations
                    <span className="ml-2 text-slate-500" data-testid="admin-pending-invitations-count">
                      ({pendingInvites.length})
                    </span>
                  </div>
                  <div className="space-y-3" data-testid="admin-pending-invitations-list">
                    {pendingInvites.map((inv) => (
                      <div
                        key={inv.membership_id}
                        className="p-4 rounded-lg bg-amber-500/5 border border-dashed border-amber-400/25"
                        data-testid={`pending-invite-card-${inv.membership_id}`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="min-w-0">
                            <div
                              className="font-medium text-white truncate"
                              data-testid={`pending-invite-name-${inv.membership_id}`}
                            >
                              {pendingDisplayName(inv)}
                            </div>
                            {pendingHasName(inv) && (
                              <div
                                className="truncate text-sm text-slate-400"
                                data-testid={`pending-invite-email-${inv.membership_id}`}
                              >
                                {inv.pending_user_email}
                              </div>
                            )}
                            <div className="mt-2 flex items-center gap-2 flex-wrap">
                              <Badge
                                className="bg-amber-500/20 text-amber-100 ring-1 ring-amber-400/20"
                                data-testid={`pending-invite-status-${inv.membership_id}`}
                              >
                                Pending
                              </Badge>
                              <span data-testid={`pending-invite-role-${inv.membership_id}`}>
                                <RoleBadge role={inv.role} />
                              </span>
                              {inv.created_at && (
                                <span
                                  className="text-xs text-slate-400"
                                  data-testid={`pending-invite-created-${inv.membership_id}`}
                                >
                                  Invited {formatInvitedAt(inv.created_at)}
                                </span>
                              )}
                            </div>
                            <div className="mt-2 text-xs text-slate-500">
                              This person has not accepted yet, so they are not an Account member.
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={cancellingInviteId === inv.membership_id}
                            onClick={() => onCancelInvite(inv.membership_id)}
                            data-testid={`pending-invite-cancel-${inv.membership_id}`}
                          >
                            {cancellingInviteId === inv.membership_id
                              ? "Cancelling..."
                              : "Cancel invitation"}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-3" data-testid="admin-users-list">
                {users.map((u) => (
                  <div key={u.id} className="p-4 rounded-lg bg-white/5 border border-white/10" data-testid={`admin-user-card-${u.id}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <div className="font-medium text-white" data-testid={`admin-user-name-${u.id}`}>
                          {u.name}
                        </div>
                        <div className="text-sm text-slate-400" data-testid={`admin-user-email-${u.id}`}>
                          {u.email}
                        </div>
                        <div className="mt-2 flex flex-wrap items-center gap-2" data-testid={`admin-user-meta-${u.id}`}>
                          {/* MA-PHASE11A5-FINAL-CONTEXT-ROLE-CLEANUP-001: No legacy role fallback */}
                          <RoleBadge role={u.membership_role || "unknown"} />
                          <Badge
                            className={
                              (u.enabled
                                ? "bg-emerald-500/15 text-emerald-300"
                                : "bg-red-500/15 text-red-300")
                            }
                            data-testid={`admin-user-enabled-badge-${u.id}`}
                          >
                            {u.enabled ? "Enabled" : "Disabled"}
                          </Badge>
                        </div>
                        <div className="mt-2" data-testid={`admin-user-id-row-${u.id}`}>
                          <code
                            className="text-xs text-slate-500 font-mono"
                            data-testid={`admin-user-id-badge-${u.id}`}
                            title={u.id}
                          >
                            ID: {u.id}
                          </code>
                        </div>
                        {/* MA-PHASE11A5: Check membership_role for sales agent toggle visibility */}
                        {(["admin", "owner"].includes(u.membership_role)) ? (
                          <div
                            className="mt-3 flex items-center gap-2 text-sm text-slate-400"
                            data-testid={`admin-user-sales-agent-row-${u.id}`}
                          >
                            <Switch
                              checked={Boolean(u.list_as_sales_agent)}
                              onCheckedChange={(checked) => onUpdateUser(u, { list_as_sales_agent: checked })}
                              className="data-[state=checked]:bg-indigo-500"
                              data-testid={`admin-user-sales-agent-toggle-${u.id}`}
                            />
                            <span data-testid={`admin-user-sales-agent-label-${u.id}`}>List as Sales Agent</span>
                          </div>
                        ) : null}
                      </div>

                      <div className="flex items-center gap-2" data-testid={`admin-user-actions-${u.id}`}>
                        <Button
                          variant={u.membership_status === "active" ? "destructive-outline" : "outline"}
                          size="sm"
                          onClick={() =>
                            u.membership_status === "active"
                              ? onChangeStatus(u, "suspended", "Membership suspended")
                              : onChangeStatus(u, "active", "Membership reinstated")
                          }
                          disabled={!canAdministerMember(u)}
                          data-testid={`admin-user-toggle-status-button-${u.id}`}
                        >
                          {u.membership_status === "active" ? "Suspend" : "Reactivate"}
                        </Button>

                    {canRemoveMember(u) ? (
                      <Dialog
                        open={deleteUserOpen && deleteUser?.id === u.id}
                        onOpenChange={(open) => {
                          setDeleteUserOpen(open);
                          setDeleteUser(open ? u : null);
                        }}
                      >
                        <DialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            data-testid={`admin-user-delete-open-${u.id}`}
                            title="Remove from Account"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="glass border-white/10" data-testid={`admin-user-delete-dialog-${u.id}`}>
                          <DialogHeader>
                            <DialogTitle className="text-slate-100">Remove from Account</DialogTitle>
                            <DialogDescription className="text-slate-200/70">
                              Removes this person&rsquo;s access to this Account. It does not
                              delete their MissionAssist identity, and it does not affect any
                              other Account they belong to. Historical records are preserved.
                            </DialogDescription>
                          </DialogHeader>
                          <div className="text-sm text-slate-200/80">
                            Remove <span className="font-semibold">{u.email}</span> from this Account?
                          </div>
                          <DialogFooter>
                            <Button
                              variant="secondary"
                              onClick={() => {
                                setDeleteUserOpen(false);
                                setDeleteUser(null);
                              }}
                            >
                              Cancel
                            </Button>
                            <Button
                              variant="destructive"
                              onClick={() => onRemoveMember(u)}
                              data-testid={`admin-user-delete-confirm-${u.id}`}
                            >
                              Remove
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    ) : null}

                    {/* P4-WP-8C: canonical Account roles, written to the
                        MEMBERSHIP through the governed route. Owner is offered
                        only to an Owner actor; legacy member/agent and
                        super_admin are deliberately absent. */}
                    <Select
                      value={u.membership_role || ""}
                      disabled={!canAdministerMember(u)}
                      onValueChange={(val) => onChangeRole(u, val)}
                    >
                      <SelectTrigger className="bg-white/5 text-slate-100 ring-1 ring-white/10" data-testid={`admin-user-role-select-${u.id}`}>
                        <SelectValue placeholder="Role" />
                      </SelectTrigger>
                      <SelectContent data-testid={`admin-user-role-select-content-${u.id}`}>
                        {userRole === "owner" ? (
                          <SelectItem value="owner" data-testid={`admin-user-role-owner-${u.id}`}>
                            Owner
                          </SelectItem>
                        ) : null}
                        <SelectItem value="admin" data-testid={`admin-user-role-admin-${u.id}`}>
                          Admin
                        </SelectItem>
                        <SelectItem value="manager" data-testid={`admin-user-role-manager-${u.id}`}>
                          Manager
                        </SelectItem>
                        <SelectItem value="sales_agent" data-testid={`admin-user-role-sales-agent-${u.id}`}>
                          Sales Agent
                        </SelectItem>
                        <SelectItem value="booking_agent" data-testid={`admin-user-role-booking-agent-${u.id}`}>
                          Booking Agent
                        </SelectItem>
                        <SelectItem value="customer_service_agent" data-testid={`admin-user-role-csa-${u.id}`}>
                          Customer Service Agent
                        </SelectItem>
                      </SelectContent>
                    </Select>

                    {isSuper ? (
                      <Select value={u.team_id || ""} onValueChange={(val) => onUpdateUser(u, { team_id: val })}>
                        <SelectTrigger className="w-36 bg-white/5 text-slate-100 border-white/10" data-testid={`admin-user-team-select-${u.id}`}>
                          <SelectValue placeholder="Account" />
                        </SelectTrigger>
                        <SelectContent data-testid={`admin-user-team-select-content-${u.id}`}>
                          {/* MA-PHASE11A-AUDIT-001: Using canonicalAccounts instead of legacy teams */}
                          {canonicalAccounts.map((t) => (
                            <SelectItem key={t.id} value={t.id} data-testid={`admin-user-team-item-${u.id}-${t.id}`}>
                              {t.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : null}

                    {/* Workspace Assignment */}
                    <UserWorkspaceAssignment 
                      user={u} 
                      onUpdate={refresh}
                    />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* MA-ADMIN-ACCESS-CENTER-UI-147: RBAC Module Permission Management */}
        {/* MA-UI-152: Design System Harmonization - Match Admin Suite Patterns */}
        <TabsContent value="access" className="mt-5 space-y-6" data-testid="admin-console-access-tab">
          {/* Standalone Page Header - Harmonized with Users/Scripts/AI sections */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-indigo-400" />
              <h2 className="text-xl font-semibold text-white" data-testid="admin-access-title">
                Access Management
              </h2>
            </div>
            <p className="text-sm text-slate-400 mt-1" data-testid="admin-access-subtitle">
              Manage module permissions and access levels for team members in this account
            </p>
          </div>

          {/* Two Independent Primary Cards */}
          <AccessCenterContent 
            users={users}
            tenantId={isSuper ? selectedTenantId : null}
          />
        </TabsContent>

        {/* MA-ADMIN-ACCESS-CENTER-UI-147-LEGACY: OLD PLAYBOOK/SCRIPT ACCESS TAB (DEPRECATED) */}
        {/* Kept for reference - can be removed after migration */}
        <TabsContent value="access-legacy" className="mt-5 hidden" data-testid="admin-console-access-legacy-tab">
          <Card className="bg-white/5 border-white/10" data-testid="admin-access-legacy-card">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-xl text-white flex items-center gap-2" data-testid="admin-access-legacy-title">
                    <Shield className="h-5 w-5 text-indigo-400" />
                    Access Control (Legacy)
                  </CardTitle>
                  <div className="text-sm text-slate-400 mt-1" data-testid="admin-access-legacy-subtitle">
                    Choose what each user can see and edit. Saving will replace their current grants.
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3" data-testid="admin-access-top-grid">
                <div className="space-y-2" data-testid="admin-access-user-picker">
                  <div className="text-sm text-slate-200/70" data-testid="admin-access-user-picker-label">
                    User
                  </div>
                  <Select value={accessUserId || ""} onValueChange={setAccessUserId}>
                    <SelectTrigger className="bg-white/5 text-slate-100 ring-1 ring-white/10" data-testid="admin-access-user-select">
                      <SelectValue placeholder="Select user" />
                    </SelectTrigger>
                    <SelectContent data-testid="admin-access-user-select-content">
                      {users.map((u) => (
                        <SelectItem key={u.id} value={u.id} data-testid={`admin-access-user-item-${u.id}`}>
                          {u.name} ({u.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="md:col-span-2 flex items-end" data-testid="admin-access-actions">
                  <Button
                    onClick={onSaveAccess}
                    disabled={!accessUserId || accessLoading}
                    data-testid="admin-access-save-button"
                  >
                    Save access
                  </Button>
                  {accessUser ? (
                    <div className="ml-3 text-sm text-slate-200/70" data-testid="admin-access-user-role-hint">
                      Editing access for: <span className="font-semibold text-slate-100">{accessUser.name}</span> • Role:{" "}
                      <span className="font-mono text-slate-100">
                        {accessUser.role === "super_admin"
                          ? "Super Admin"
                          : accessUser.role === "admin"
                            ? "Admin"
                            : accessUser.role === "booking_agent"
                              ? "Booking Agent"
                              : "Sales Agent"}
                      </span>
                    </div>
                  ) : null}
                </div>
              </div>

              {accessUser?.role === "agent" ? (
                <div className="rounded-xl border border-white/10 bg-amber-500/10 p-3 text-sm text-amber-100" data-testid="admin-access-agent-note">
                  Sales Agents and Booking Agents are <b>run-only</b>. Any “edit” toggles will be ignored.
                </div>
              ) : null}

              <Separator className="bg-white/10" />

              <div className="grid grid-cols-1 gap-4 lg:grid-cols-12" data-testid="admin-access-grid">
                <Card className="border border-white/10 bg-white/5 lg:col-span-5" data-testid="admin-access-product-types-card">
                  <CardHeader>
                    <CardTitle className="text-slate-100" data-testid="admin-access-product-types-title">
                      Playbooks
                    </CardTitle>
                    <div className="text-sm text-slate-200/70" data-testid="admin-access-product-types-subtitle">
                      Grant access by playbook (recommended).
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3" data-testid="admin-access-product-types-list">
                    {productTypes.length === 0 ? (
                      <div className="text-sm text-slate-200/70" data-testid="admin-access-product-types-empty">
                        No playbooks found.
                      </div>
                    ) : (
                      productTypes.map((pt) => (
                        <div key={pt.id} className="rounded-xl border border-white/10 bg-black/20 p-3" data-testid={`admin-access-product-type-row-${pt.id}`}>
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <div className="text-sm font-semibold text-slate-100" data-testid={`admin-access-product-type-name-${pt.id}`}>
                                {pt.name}
                              </div>
                              <div className="text-xs font-mono text-slate-200/60" data-testid={`admin-access-product-type-id-${pt.id}`}>
                                {pt.id}
                              </div>
                            </div>

                            <div className="flex items-center gap-3" data-testid={`admin-access-product-type-toggles-${pt.id}`}>
                              <div className="flex items-center gap-2" data-testid={`admin-access-product-type-view-toggle-${pt.id}`}>
                                <Checkbox
                                  checked={Boolean(ptViewIds[pt.id])}
                                  onCheckedChange={(val) => setPtViewIds((prev) => ({ ...prev, [pt.id]: Boolean(val) }))}
                                  className="border-slate-400 data-[state=checked]:bg-indigo-500 data-[state=checked]:border-indigo-500"
                                  data-testid={`admin-access-product-type-view-checkbox-${pt.id}`}
                                />
                                <span className="text-sm text-slate-200/80">View</span>
                              </div>

                              <div className="flex items-center gap-2" data-testid={`admin-access-product-type-edit-toggle-${pt.id}`}>
                                <Checkbox
                                  checked={Boolean(ptEditIds[pt.id])}
                                  onCheckedChange={(val) => setPtEditIds((prev) => ({ ...prev, [pt.id]: Boolean(val) }))}
                                  disabled={!ptViewIds[pt.id] || !canGrantEditToRole(accessUser?.role)}
                                  className="border-slate-400 data-[state=checked]:bg-indigo-500 data-[state=checked]:border-indigo-500"
                                  data-testid={`admin-access-product-type-edit-checkbox-${pt.id}`}
                                />
                                <span className="text-sm text-slate-200/80">Edit</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </CardContent>
                </Card>

                <Card className="border border-white/10 bg-white/5 lg:col-span-7" data-testid="admin-access-scripts-card">
                  <CardHeader>
                    <CardTitle className="text-slate-100" data-testid="admin-access-scripts-title">
                      Individual Scripts
                    </CardTitle>
                    <div className="text-sm text-slate-200/70" data-testid="admin-access-scripts-subtitle">
                      Use this to grant only specific scripts.
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3" data-testid="admin-access-scripts-list">
                    {scripts.length === 0 ? (
                      <div className="text-sm text-slate-200/70" data-testid="admin-access-scripts-empty">
                        No scripts found.
                      </div>
                    ) : (
                      scripts.slice(0, 60).map((s) => (
                        <div key={s.id} className="rounded-xl border border-white/10 bg-black/20 p-3" data-testid={`admin-access-script-row-${s.id}`}>
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="truncate text-sm font-semibold text-slate-100" data-testid={`admin-access-script-name-${s.id}`}>
                                {s.name}
                              </div>
                              <div className="mt-1 text-xs font-mono text-slate-200/60">{s.id}</div>
                            </div>

                            <div className="flex items-center gap-3" data-testid={`admin-access-script-toggles-${s.id}`}>
                              <div className="flex items-center gap-2" data-testid={`admin-access-script-view-toggle-${s.id}`}>
                                <Checkbox
                                  checked={Boolean(scriptViewIds[s.id])}
                                  onCheckedChange={(val) => setScriptViewIds((prev) => ({ ...prev, [s.id]: Boolean(val) }))}
                                  className="border-slate-400 data-[state=checked]:bg-indigo-500 data-[state=checked]:border-indigo-500"
                                  data-testid={`admin-access-script-view-checkbox-${s.id}`}
                                />
                                <span className="text-sm text-slate-200/80">View</span>
                              </div>

                              <div className="flex items-center gap-2" data-testid={`admin-access-script-edit-toggle-${s.id}`}>
                                <Checkbox
                                  checked={Boolean(scriptEditIds[s.id])}
                                  onCheckedChange={(val) => setScriptEditIds((prev) => ({ ...prev, [s.id]: Boolean(val) }))}
                                  disabled={!scriptViewIds[s.id] || !canGrantEditToRole(accessUser?.role)}
                                  className="border-slate-400 data-[state=checked]:bg-indigo-500 data-[state=checked]:border-indigo-500"
                                  data-testid={`admin-access-script-edit-checkbox-${s.id}`}
                                />
                                <span className="text-sm text-slate-200/80">Edit</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="scripts" className="mt-5" data-testid="admin-console-scripts-tab">
          <Card className="bg-white/5 border-white/10" data-testid="admin-scripts-card">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-xl text-white flex items-center gap-2" data-testid="admin-scripts-title">
                    <TestTubeDiagonal className="h-5 w-5 text-indigo-400" />
                    Scripts
                  </CardTitle>
                  <div className="mt-1 text-sm text-slate-400" data-testid="admin-scripts-subtitle">
                    Manage account-scoped scripts and copy between accounts
                  </div>
                </div>

                {isSuper ? (
                  <Dialog open={copyOpen} onOpenChange={setCopyOpen}>
                    <DialogTrigger asChild>
                      <Button size="sm" data-testid="admin-copy-script-open">
                        <Copy className="mr-2 h-4 w-4" /> Copy script
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-slate-950 text-slate-100 ring-1 ring-white/10">
                      <DialogHeader>
                        <DialogTitle data-testid="admin-copy-script-title">Copy script between accounts</DialogTitle>
                        <DialogDescription className="text-slate-200/70" data-testid="admin-copy-script-desc">
                          Copies the full script graph (nodes + options) into a target account/playbook/script type.
                        </DialogDescription>
                      </DialogHeader>

                    <div className="space-y-4" data-testid="admin-copy-script-form">
                      <div className="space-y-2">
                        <div className="text-sm text-slate-200/70">From script</div>
                        <Select value={copyFromScriptId || ""} onValueChange={setCopyFromScriptId}>
                          <SelectTrigger className="bg-white/5 text-slate-100 ring-1 ring-white/10">
                            <SelectValue placeholder={scripts.length ? "Select script" : "No scripts"} />
                          </SelectTrigger>
                          <SelectContent>
                            {scripts.map((s) => (
                              <SelectItem key={s.id} value={s.id}>
                                {s.name || "(untitled)"}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <div className="text-sm text-slate-200/70">New name</div>
                        <Input
                          value={copyName}
                          onChange={(e) => setCopyName(e.target.value)}
                          className="bg-white/5 text-slate-100 ring-1 ring-white/10"
                          placeholder="Copied script name"
                          data-testid="admin-copy-script-name"
                        />
                      </div>

                      <Separator className="bg-white/10" />

                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div className="space-y-2">
                          <div className="text-sm text-slate-200/70">Target account</div>
                          <Select value={copyToTeamId || ""} onValueChange={setCopyToTeamId}>
                            <SelectTrigger className="bg-white/5 text-slate-100 ring-1 ring-white/10">
                              <SelectValue placeholder="Select account" />
                            </SelectTrigger>
                            <SelectContent>
                              {/* MA-PHASE11A-AUDIT-001: Using canonicalAccounts instead of legacy teams */}
                              {canonicalAccounts.map((t) => (
                                <SelectItem key={t.id} value={t.id}>
                                  {t.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <div className="text-sm text-slate-200/70">Target playbook</div>
                          <Select value={copyToProductTypeId || ""} onValueChange={setCopyToProductTypeId}>
                            <SelectTrigger className="bg-white/5 text-slate-100 ring-1 ring-white/10">
                              <SelectValue placeholder={copyTargetProductTypes.length ? "Select playbook" : "No playbooks"} />
                            </SelectTrigger>
                            <SelectContent>
                              {copyTargetProductTypes.map((pt) => (
                                <SelectItem key={pt.id} value={pt.id}>
                                  {pt.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2 sm:col-span-2">
                          <div className="text-sm text-slate-200/70">Target script type</div>
                          <Select value={copyToScriptTypeId || ""} onValueChange={setCopyToScriptTypeId}>
                            <SelectTrigger className="bg-white/5 text-slate-100 ring-1 ring-white/10">
                              <SelectValue placeholder={copyTargetScriptTypes.length ? "Select script type" : "No script types"} />
                            </SelectTrigger>
                            <SelectContent>
                              {copyTargetScriptTypes.map((st) => (
                                <SelectItem key={st.id} value={st.id}>
                                  {st.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>

                    <DialogFooter>
                      <Button
                        variant="secondary"
                        onClick={() => setCopyOpen(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        disabled={
                          copyBusy ||
                          !copyFromScriptId ||
                          !copyToTeamId ||
                          !copyToProductTypeId ||
                          !copyToScriptTypeId ||
                          !copyName.trim()
                        }
                        onClick={onCopyScript}
                        data-testid="admin-copy-script-submit"
                      >
                        {copyBusy ? "Copying…" : "Copy"}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
                ) : null}
              </div>
            </CardHeader>

            <CardContent className="space-y-3" data-testid="admin-scripts-list">
              {scripts.length === 0 ? (
                <div className="text-sm text-slate-200/70" data-testid="admin-scripts-empty">
                  No scripts found.
                </div>
              ) : (
                scripts.map((s) => (
                  <div
                    key={s.id}
                    className="flex flex-col gap-2 rounded-2xl border border-white/10 bg-white/5 p-4 sm:flex-row sm:items-center sm:justify-between"
                    data-testid={`admin-script-row-${s.id}`}
                  >
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold text-slate-100">{s.name || "(untitled)"}</div>
                      <div className="mt-1 text-xs font-mono text-slate-200/60">{s.id}</div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      asChild
                    >
                      <Link to={`/app/scripts/${s.id}/edit`}>Open</Link>
                    </Button>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Workspaces Tab */}
        {isSuper || isAdmin ? (
          <TabsContent value="workspaces" className="mt-5" data-testid="admin-console-workspaces-tab">
            <WorkspaceAdminManager tenantId={isSuper ? selectedTenantId : null} />
          </TabsContent>
        ) : null}

        {isSuper ? (
          <TabsContent value="ai" className="mt-5" data-testid="admin-console-ai-tab">
            <Card className="bg-white/5 border-white/10" data-testid="admin-ai-card">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-xl text-white flex items-center gap-2" data-testid="admin-ai-title">
                      <Sparkles className="h-5 w-5 text-indigo-400" />
                      AI Providers
                    </CardTitle>
                    <div className="mt-1 text-sm text-slate-400" data-testid="admin-ai-subtitle">
                      Connect your OpenAI / Anthropic API keys (BYOK). Keys are encrypted at rest.
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4" data-testid="admin-ai-content">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="rounded-2xl bg-white/5 p-4 ring-1 ring-white/10" data-testid="admin-ai-openai-status">
                    <div className="flex items-center justify-between">
                      <div className="space-y-2">
                        <div className="text-sm text-slate-100">OpenAI (ChatGPT)</div>
                        <Badge
                          className={
                            (llmStatus.openai ? "bg-indigo-500/20 text-indigo-100 ring-indigo-400/20" : "bg-white/10 text-slate-100 ring-white/10") +
                            " ring-1"
                          }
                        >
                          {llmStatus.openai ? "Connected" : "Not connected"}
                        </Badge>
                      </div>
                      <Button
                        variant="destructive-outline"
                        size="sm"
                        onClick={() => onDisconnectLlmKey("openai")}
                        disabled={!llmStatus.openai || llmBusy}
                      >
                        Disconnect
                      </Button>
                    </div>
                  </div>

                  <div className="rounded-2xl bg-white/5 p-4 ring-1 ring-white/10" data-testid="admin-ai-anthropic-status">
                    <div className="flex items-center justify-between">
                      <div className="space-y-2">
                        <div className="text-sm text-slate-100">Anthropic (Claude)</div>
                        <Badge
                          className={
                            (llmStatus.anthropic
                              ? "bg-indigo-500/20 text-indigo-100 ring-indigo-400/20"
                              : "bg-white/10 text-slate-100 ring-white/10") + " ring-1"
                          }
                        >
                          {llmStatus.anthropic ? "Connected" : "Not connected"}
                        </Badge>
                      </div>
                      <Button
                        variant="destructive-outline"
                        size="sm"
                        onClick={() => onDisconnectLlmKey("anthropic")}
                        disabled={!llmStatus.anthropic || llmBusy}
                      >
                        Disconnect
                      </Button>
                    </div>
                  </div>
                </div>

                <Separator className="bg-white/10" />

                <div className="grid grid-cols-1 gap-3 lg:grid-cols-12" data-testid="admin-ai-form">
                  <div className="lg:col-span-3">
                    <div className="text-sm text-slate-200/70">Provider</div>
                    <Select value={llmProvider} onValueChange={setLlmProvider}>
                      <SelectTrigger className="mt-1 bg-white/5 text-slate-100 ring-1 ring-white/10">
                        <SelectValue placeholder="Provider" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="openai">OpenAI (ChatGPT)</SelectItem>
                        <SelectItem value="anthropic">Anthropic (Claude)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="lg:col-span-3">
                    <div className="text-sm text-slate-200/70">Model</div>
                    <Input
                      value={llmModel}
                      onChange={(e) => setLlmModel(e.target.value)}
                      className="mt-1 bg-white/5 text-slate-100 ring-1 ring-white/10"
                      placeholder={llmProvider === "openai" ? "gpt-5.1" : "claude-4-sonnet-20250514"}
                      data-testid="admin-ai-model"
                    />
                  </div>

                  <div className="lg:col-span-6">
                    <div className="text-sm text-slate-200/70">API key</div>
                    <Input
                      value={llmApiKey}
                      onChange={(e) => setLlmApiKey(e.target.value)}
                      className="mt-1 bg-white/5 text-slate-100 ring-1 ring-white/10"
                      placeholder={llmProvider === "openai" ? "sk-..." : "sk-ant-..."}
                      type="password"
                      data-testid="admin-ai-api-key"
                    />
                  </div>

                  <div className="lg:col-span-12 flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <div className="text-sm text-slate-200/80">Keys are stored per-user on this account.</div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={onTestLlmKey}
                        disabled={llmTestBusy || llmBusy || (!llmStatus[llmProvider] && !llmApiKey.trim())}
                        data-testid="admin-ai-test"
                      >
                        <TestTubeDiagonal className="mr-2 h-4 w-4" />
                        {llmTestBusy ? "Testing…" : "Test"}
                      </Button>

                      <Button
                        onClick={onSaveLlmKey}
                        disabled={llmBusy || !llmApiKey.trim()}
                        data-testid="admin-ai-save"
                      >
                        Save key
                      </Button>
                    </div>
                  </div>

                  {llmTestResult ? (
                    <div
                      className="lg:col-span-12 rounded-2xl bg-black/20 p-3 text-xs text-slate-200/80 ring-1 ring-white/10"
                      data-testid="admin-ai-test-result"
                    >
                      <pre className="whitespace-pre-wrap">{JSON.stringify(llmTestResult, null, 2)}</pre>
                    </div>
                  ) : null}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        ) : null}

        {/* Lead Sources Tab */}
        {isSuper || isAdmin ? (
          <TabsContent value="lead-sources" className="mt-5" data-testid="admin-console-lead-sources-tab">
            <LeadSourceAdminManager tenantId={isSuper ? selectedTenantId : null} />
          </TabsContent>
        ) : null}

        {/* Inbound Setup Tab - Posting Instructions & Status */}
        {isSuper || isAdmin ? (
          <TabsContent value="inbound-setup" className="mt-5" data-testid="admin-console-inbound-setup-tab">
            <InboundSetupManager tenantId={selectedTenantId} />
          </TabsContent>
        ) : null}

        {/* Compliance Tab - MA-ADMIN-001: Global TrustedForm API key management */}
        {isSuper || isAdmin ? (
          <TabsContent value="compliance" className="mt-5" data-testid="admin-console-compliance-tab">
            <ComplianceSettingsManager tenantId={isSuper ? selectedTenantId : null} />
          </TabsContent>
        ) : null}

        {/* Outbound Setup Tab - MA-UI-108: Outbound routing destinations */}
        {isSuper || isAdmin ? (
          <TabsContent value="outbound-setup" className="mt-5" data-testid="admin-console-outbound-setup-tab">
            <OutboundSetupManager tenantId={isSuper ? selectedTenantId : null} />
          </TabsContent>
        ) : null}

        {/* Calendars Tab - Webhook Key Management + Calendar Routing */}
        {isSuper || isAdmin ? (
          <TabsContent value="calendars" className="mt-5" data-testid="admin-console-calendars-tab">
            <div className="space-y-6">
              {/* Webhook Key Management - Top Card */}
              <WebhookKeyCard tenantId={isSuper ? selectedTenantId : null} />
              
              {/* Calendar Routes */}
              <CalendarRoutingManager tenantId={isSuper ? selectedTenantId : null} />
            </div>
          </TabsContent>
        ) : null}

        {/* Vocabulary Tab - Channels, Verticals, Platforms */}
        {isSuper || isAdmin ? (
          <TabsContent value="vocabulary" className="mt-5" data-testid="admin-console-vocabulary-tab">
            <VocabularyManager />
          </TabsContent>
        ) : null}

        {/* Mapping Tab - Vertical Mappings, Unmapped Campaigns/Forms */}
        {isSuper || isAdmin ? (
          <TabsContent value="mapping" className="mt-5" data-testid="admin-console-mapping-tab">
            <VerticalMappingsManager tenantId={isSuper ? selectedTenantId : null} />
          </TabsContent>
        ) : null}

        {/* Analytics Tab - Benchmarks */}
        {isSuper || isAdmin ? (
          <TabsContent value="analytics" className="mt-5" data-testid="admin-console-analytics-tab">
            <BenchmarkManager tenantId={isSuper ? selectedTenantId : null} />
          </TabsContent>
        ) : null}

        {/* Diagnostics Tab - Inbound + Outbound Events with Health Summary */}
        {isSuper || isAdmin ? (
          <TabsContent value="diagnostics" className="mt-5" data-testid="admin-console-diagnostics-tab">
            <div className="space-y-6">
              {/* Intake & Outbound Events - Primary operational visibility */}
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                {/* Intake Events (Inbound) */}
                <IntakeEventsViewer tenantId={isSuper ? selectedTenantId : null} />
                
                {/* Outbound Events (Meta CAPI, etc.) */}
                <OutboundEventsViewer tenantId={isSuper ? selectedTenantId : null} />
              </div>
              
              {/* Queue Jobs - Secondary debug layer (MA-ADMIN-009) */}
              <QueueJobsViewer tenantId={isSuper ? selectedTenantId : null} />
            </div>
          </TabsContent>
        ) : null}

        {/* Quarantine Tab - Quarantined Appointments */}
        {isSuper || isAdmin ? (
          <TabsContent value="quarantine" className="mt-5" data-testid="admin-console-quarantine-tab">
            <QuarantineQueueContent tenantId={isSuper ? selectedTenantId : null} />
          </TabsContent>
        ) : null}
      </Tabs>
    </div>
  );
}
